/* eslint-disable no-restricted-globals */
// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  SECTION2WITHIDLABELS.JS - VERSION: 2026.01.08.2000                        ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2026.01.08.2000
// ============================================================================
// CHANGELOG:
// 2026.01.08.2000 - FIX: Buy Back now costs 25% (not 100%), Add Ask a Pal button
// 2026.01.08.1900 - FIX: Wait for THIS GAME's pictures (gamePictures not pictures)
// 2025.12.23.1500 - UI: New Time Out + Brain Glitch screens with speed adjustment
// 2025.12.18.2030 - FIX: Game position now correctly resumes (race condition fix)
// 2025.12.15.2215 - FIX: Group Quiz review no longer shows doubled questions
// 2025.12.15.2200 - Removed debug console.log from answer filtering
// 2025.12.14.2150 - Group Quiz: moved countdown timer to top right, removed static text
// 2025.12.14.1900 - Removed duplicate contact button (now in App.js FloatingContactButton)
// 2025.12.14.1845 - FIX: Solo timer now counts down (replaced over-complicated code)
// 2025.12.13.1545 - CRITICAL FIX: Duplicate milestone rewards - now checks DB BEFORE creating
// 2025.12.13.1400 - FIX: Question position now persists correctly after game exit
// 2025.12.13.1400 - FIX: game_id type comparison uses String() for type safety
// 2025.12.12.1845 - FIX: Sponsor banner now uses grid_message (not sponsorMessage)
// 2025.12.04.1735 - Previous version
// 2025.12.04.0003 - Added rewards history logging (milestones, grid bonuses, correct answers)
// 2025.12.04.0002 - FIX: Milestone celebration only triggers on TRAK increase, not on game start
// 2025.12.03.0001 - FIX: Game name headers, DOM fixes, Picture Grid default ON, Timer waits for overlays
// 2025.12.02.1830 - Fixed answer screen layout - all elements 340px wide and centered
// 2025.12.02.1820 - Fixed singular/plural for tags/TRAKs, added total stats to header
// 2025.12.02.1810 - Registration prompt after 16 questions for anonymous players
// 2025.12.02.1800 - Pass session stats to onExitGame for database sync
// 2025.12.02.1630 - Fixed questions loading with useMemo, fixed difficulty_points priority
// 2025.12.02.1600 - Load questions from API prop instead of localStorage
// 2025.11.30.0200 - Replaced TT fallback with favicon in all header locations
// 2025.11.30.0155 - Exit Ad screen with countdown, date range & impression checks
// 2025.11.30.0150 - Fixed syntax error (orphaned else block from sed cleanup)
// 2025.11.30.0145 - Removed debug console.logs (47), unused getGameId() - 2.5KB savings
// 2025.11.29.0125 - Per-game position tracking (Group Quiz backwards, Player Quiz forwards)
// 2025.11.29.0115 - Challenge button orange, PRESS TO START button depth (py-3)
// 2025.11.29.0110 - Tightened 10s/15s layout per wireframe, compact spacing
// 2025.11.29.0105 - Wireframe layout implementation
// 2025.11.29.0100 - Answer screen header with favicon + GROUP QUIZ + quiz name
// 2025.11.29.0055 - Countdown pulse animation, Challenge/Review buttons moved to bottom
// 2025.11.28.XXXX - Group Quiz timer modes, exit flow corrections
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle, Flag, MessageSquare } from 'lucide-react';
import PictureGridScreen from './PictureGridGame';
import { ACHIEVEMENT_MILESTONES, getCurrentAchievement, checkMilestoneReached, getUnclaimedMilestones } from '../achievementHelpers';
import { logAchievementReward, logGridBonusReward, logCorrectAnswerReward } from '../rewardsApi';
import { getToken } from '../api'; // 🔧 FIX 2025.12.13: For milestone claim API call

// API URL for milestone claims
const API_URL = 'https://api.traktag.com/api';

// ============================================================================
// QUESTION SCREEN WITH CHALLENGE POPUP INTEGRATED
// ============================================================================
function Section2WithIDLabels({ 
  onSubmitChallenge,
  playerData,
  currentMode,
  modeStats,
  setModeStats,
  groupQuizMode,
  setGroupQuizMode,
  groupQuizTimer,
  setGroupQuizTimer,
  questionsInSession,
  setQuestionsInSession,
  selectedGame,
  selectedGameId,    // 🔧 FIX 2025.12.04: Added for rewards logging
  games,           // 🆕 NEW
  pictures,        // 🆕 NEW
  questions = [],  // 🆕 NEW: Questions from API
  pictureGridSettings,
  gridTappedCells,
  setGridTappedCells,
  currentPictureIndex,
  setCurrentPictureIndex,
  compactLogoUrl,  // 🆕 NEW: Logo for header
  onExitGame,      // 🆕 NEW: Return to home
  isLoggedIn,      // 🆕 NEW: Check if player is registered
  onShowAuth       // 🆕 NEW: Trigger auth screen for registration
}) {
  // 🛡️ ANTI-CRASH: Safe fallback for currentMode (prevents crash when props are undefined during render)
  const safeCurrentMode = currentMode || 'relaxed';
  
  const ID = ({ num }) => (
    <div style={{
      position: 'absolute',
      top: '2px',
      right: '2px',
      background: 'red',
      color: 'white',
      fontSize: '11px',
      fontWeight: 'bold',
      padding: '3px 7px',
      borderRadius: '4px',
      zIndex: 10000,
      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
    }}>
      #{num}
    </div>
  );
  
  // 🆕 UPDATED: Use questions prop from API, filter by selected game
  // Use useMemo to make it reactive when questions prop changes
  const questionDatabase = useMemo(() => {
    // Use questions prop passed from App.js (loaded from API)
    if (questions && questions.length > 0) {
      // Filter questions for the selected game
      if (selectedGame && selectedGame.game_id) {
        // 🔧 FIX 2025.12.13: Use String() comparison for type safety
        const gameQuestions = questions.filter(q => 
          String(q.game_id) === String(selectedGame.game_id) && q.active !== false
        );
        
        console.log(`📚 Loaded ${gameQuestions.length} questions from API for game: ${selectedGame.name || selectedGame.game_name} (ID: ${selectedGame.game_id})`);
        
        if (gameQuestions.length > 0) {
          return gameQuestions;
        }
      }
    }
    
    // Fallback to localStorage if API questions not available
    const storedQuestions = localStorage.getItem('questions');
    
    if (storedQuestions) {
      try {
        const allQuestions = JSON.parse(storedQuestions);
        
        // Filter questions for the selected game
        if (selectedGame && selectedGame.game_id) {
          // 🔧 FIX 2025.12.13: Use String() comparison for type safety
          const gameQuestions = allQuestions.filter(q => 
            String(q.game_id) === String(selectedGame.game_id) && q.active !== false
          );
          
          console.log(`📚 Loaded ${gameQuestions.length} questions from localStorage for game: ${selectedGame.game_name || selectedGame.name} (ID: ${selectedGame.game_id})`);
          
          if (gameQuestions.length > 0) {
            return gameQuestions;
          }
        }
      } catch (error) {
        console.error('Error loading questions from localStorage:', error);
      }
    }
    
    // 🔧 STANDARDIZED: Fallback test questions use database format (difficulty_points, not tag_value)
    console.log('⚠️ No questions available, using fallback test questions');
    return [
      { difficulty_points: 6, question_text: "In what year did Neil Armstrong become the first human to walk on the Moon?", correct_answer: "1969", wrong_answer1: "1968", wrong_answer2: "1970", wrong_answer3: "1971", game_id: 1, active: true },
      { difficulty_points: 1, question_text: "Who is project managing this game?", correct_answer: "Mark", wrong_answer1: "Imanuel", wrong_answer2: "Emmanuel", wrong_answer3: "Sara", game_id: 1, active: true },
      { difficulty_points: 50, question_text: "Where is Barry?", correct_answer: "South Wales", wrong_answer1: "England", wrong_answer2: "Pakistan", wrong_answer3: "India", game_id: 1, active: true },
      { difficulty_points: 20, question_text: "Where is the Whitmore Bay?", correct_answer: "Barry", wrong_answer1: "Southend", wrong_answer2: "Formby", wrong_answer3: "Blackpool", game_id: 1, active: true }
    ];
  }, [questions, selectedGame]);
  
  const modeConfig = {
    relaxed: {
      name: '🚶 Scenic TRAK',
      timer: null,
      tagsMultiplier: 1.0,
      askAPalAllowed: true,
      description: 'No timer • Base tags • Ask a Pal available',
      tagline: 'Perfect for learning - no pressure, no rush!',
      analogy: 'Like taking a walk - enjoy the scenery, take your time'
    },
    thinker: {
      name: '🚌 Local TRAK',
      timer: 15,
      tagsMultiplier: 1.25,
      askAPalAllowed: false,
      description: '15 seconds • +25% tags',
      tagline: 'Steady pace with manageable time. Build confidence!',
      analogy: 'Like a local bus - reliable, comfortable, gets you there'
    },
    speed: {
      name: '🚄 Express TRAK',
      timer: 10,
      tagsMultiplier: 1.5,
      askAPalAllowed: false,
      description: '10 seconds • +50% tags',
      tagline: 'Fast-paced challenge. Think quick, earn more!',
      analogy: 'Like an express train - fast, efficient, direct'
    },
    lightning: {
      name: '🚀 Bullet TRAK',
      timer: 5,
      tagsMultiplier: 2.0,
      askAPalAllowed: false,
      description: '5 seconds • +100% tags (DOUBLE!)',
      tagline: 'Ultimate challenge! Lightning reflexes, double rewards!',
      analogy: 'Like a rocket - extreme speed, massive payoff'
    }
  };
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [shuffledAnswers, setShuffledAnswers] = useState([]);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(0);
  
  // 🔧 FIX 2025.11.29: Per-game position tracking
  // Group Quiz: Starts at END, goes backwards
  // Player Quiz: Starts at 0, goes forwards
  
  // 🔧 FIX 2025.12.16: Track if initial position restoration is complete
  const [positionInitialized, setPositionInitialized] = useState(false);
  
  // Group Quiz Position - starts at END, decrements
  const [groupQuizPosition, setGroupQuizPosition] = useState(() => {
    const gameId = selectedGame?.game_id || 'default';
    const saved = localStorage.getItem(`groupQuizPosition_${gameId}`);
    const totalQuestions = questionDatabase.length;
    
    if (saved !== null) {
      const pos = parseInt(saved, 10);
      // Validate position is still valid (in case Qs were deleted)
      if (pos >= 0 && pos < totalQuestions) {
        return pos;
      }
    }
    // First time OR invalid: Start at END (last question)
    return totalQuestions > 0 ? totalQuestions - 1 : 0;
  });
  
  // Player Quiz Position - starts at 0, increments
  const [playerQuizPosition, setPlayerQuizPosition] = useState(() => {
    const gameId = selectedGame?.game_id || 'default';
    const saved = localStorage.getItem(`playerQuizPosition_${gameId}`);
    const totalQuestions = questionDatabase.length;
    
    if (saved !== null) {
      const pos = parseInt(saved, 10);
      // Validate position is still valid (in case Qs were deleted)
      if (pos >= 0 && pos < totalQuestions) {
        return pos;
      }
    }
    // First time OR invalid: Start at BEGINNING (first question)
    return 0;
  });
  
  // Sync currentQuestionIndex with playerQuizPosition for Player mode
  useEffect(() => {
    if (!groupQuizMode) {
      setCurrentQuestionIndex(playerQuizPosition);
    }
  }, [playerQuizPosition, groupQuizMode]);
  
  // 🔧 FIX 2025.12.16: Re-validate saved position when questions load
  // This fixes the race condition where useState ran before questions were loaded
  useEffect(() => {
    if (questionDatabase.length > 0 && !groupQuizMode) {
      const gameId = selectedGame?.game_id || 'default';
      const saved = localStorage.getItem(`playerQuizPosition_${gameId}`);
      
      if (saved !== null) {
        const savedPos = parseInt(saved, 10);
        // If saved position is valid and different from current, restore it
        if (savedPos >= 0 && savedPos < questionDatabase.length && savedPos !== playerQuizPosition) {
          console.log(`🔄 Restoring player position to ${savedPos} (was ${playerQuizPosition})`);
          setPlayerQuizPosition(savedPos);
          setCurrentQuestionIndex(savedPos);
        }
      }
      // Mark initialization complete AFTER restoration attempt
      setPositionInitialized(true);
    }
    
    // Also validate group quiz position
    if (questionDatabase.length > 0 && groupQuizMode) {
      const gameId = selectedGame?.game_id || 'default';
      const saved = localStorage.getItem(`groupQuizPosition_${gameId}`);
      
      if (saved !== null) {
        const savedPos = parseInt(saved, 10);
        if (savedPos >= 0 && savedPos < questionDatabase.length && savedPos !== groupQuizPosition) {
          console.log(`🔄 Restoring group quiz position to ${savedPos} (was ${groupQuizPosition})`);
          setGroupQuizPosition(savedPos);
        }
      } else {
        // No saved position - start at end for group quiz
        const endPos = questionDatabase.length - 1;
        if (groupQuizPosition !== endPos) {
          setGroupQuizPosition(endPos);
        }
      }
      // Mark initialization complete AFTER restoration attempt
      setPositionInitialized(true);
    }
  }, [questionDatabase.length, selectedGame?.game_id, groupQuizMode]);
  
  // Save positions to localStorage whenever they change
  // 🔧 FIX 2025.12.16: Only save AFTER initial position restoration is complete
  useEffect(() => {
    if (!positionInitialized) return; // Don't save until we've restored the correct position
    const gameId = selectedGame?.game_id || 'default';
    localStorage.setItem(`groupQuizPosition_${gameId}`, groupQuizPosition.toString());
    console.log(`💾 Saved group quiz position: ${groupQuizPosition} for game ${gameId}`);
  }, [groupQuizPosition, selectedGame?.game_id, positionInitialized]);
  
  useEffect(() => {
    if (!positionInitialized) return; // Don't save until we've restored the correct position
    const gameId = selectedGame?.game_id || 'default';
    localStorage.setItem(`playerQuizPosition_${gameId}`, playerQuizPosition.toString());
    console.log(`💾 Saved player quiz position: ${playerQuizPosition} for game ${gameId}`);
  }, [playerQuizPosition, selectedGame?.game_id, positionInitialized]);
  
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showQuestion, setShowQuestion] = useState(true);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false); // 🔧 FIX 2025.11.28: Delay End Game/Challenge buttons
  const [viewOnlyAnswer, setViewOnlyAnswer] = useState(false); // ✏️ NEW: For Skip to Next (no points)
  const [correctInGame, setCorrectInGame] = useState(0);
  const [attemptedInGame, setAttemptedInGame] = useState(0);
  const [traksInGame, setTraksInGame] = useState(0);
  const [savedTraks, setSavedTraks] = useState(0);
  const [totalTraks, setTotalTraks] = useState(0);
  const [tagsInGame, setTagsInGame] = useState(0);
  const [totalTags, setTotalTags] = useState(0);
  const [watchingAd, setWatchingAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(5);
  const [isRetry, setIsRetry] = useState(false);
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
  
  // 🆕 2025.12.23: Time Out Screen + Speed Adjustment states
  const [showTimeOutScreen, setShowTimeOutScreen] = useState(false);
  const [selectedSpeedMode, setSelectedSpeedMode] = useState(() => {
    return localStorage.getItem('preferredSpeedMode') || 'speed'; // default 10s
  });
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  
  // Speed mode options for adjustment
  const speedOptions = [
    { id: 'relaxed', icon: '🚶', name: 'None', timer: null, multiplier: '1x' },
    { id: 'thinker', icon: '🚌', name: '15s', timer: 15, multiplier: '1.25x' },
    { id: 'speed', icon: '🚄', name: '10s', timer: 10, multiplier: '1.5x' },
    { id: 'lightning', icon: '🚀', name: '6s', timer: 6, multiplier: '2x' }
  ];
  
  // 🆕 Exit Ad State
  const [showExitAd, setShowExitAd] = useState(false);
  const [exitAdCountdown, setExitAdCountdown] = useState(5);
  
  // Group Quiz Mode: End quiz and summary
  const [quizEnded, setQuizEnded] = useState(false);
  const [questionHistory, setQuestionHistory] = useState([]);
  
  // Interstitial ad tracking
  const [playCount, setPlayCount] = useState(0);
  const [lastRewardAdPlayCount, setLastRewardAdPlayCount] = useState(null);
  const [showInterstitialAd, setShowInterstitialAd] = useState(false);
  const [interstitialAdCountdown, setInterstitialAdCountdown] = useState(0);
  const [showVictory, setShowVictory] = useState(false);
  
  // 🆕 Registration prompt for anonymous players after 16 questions
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  
  // ============================================
  // 🎮 SWIPE GESTURE STATE
  // ============================================
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [victoryMessage, setVictoryMessage] = useState('');
  const [hoveringButton, setHoveringButton] = useState(false); // ✏️ NEW: Pause auto-advance on hover
  
  // Picture Grid State for Player Mode
  // 🔧 FIX 2026.01.08.1600: Initialize from localStorage to show grid immediately on mount
  const [showPictureGrid, setShowPictureGrid] = useState(() => {
    if (groupQuizMode) return false; // Never show grid in group mode
    const savedGridSetting = localStorage.getItem('pictureGridEnabled');
    return savedGridSetting === null ? true : savedGridSetting === 'true';
  });
  const [waitingForGridComplete, setWaitingForGridComplete] = useState(() => {
    if (groupQuizMode) return false;
    const savedGridSetting = localStorage.getItem('pictureGridEnabled');
    return savedGridSetting === null ? true : savedGridSetting === 'true';
  });
  const [quizStarted, setQuizStarted] = useState(false);
  
  const [showChallengePopup, setShowChallengePopup] = useState(false);
  const [challengeIssue, setChallengeIssue] = useState('');
  const [challengeDetails, setChallengeDetails] = useState('');
  const [lastAttemptedQuestion, setLastAttemptedQuestion] = useState(null);
  
  // Ask a Pal feature states
  const [palUnlocked, setPalUnlocked] = useState(false);
  const [palUsed, setPalUsed] = useState(false);
  const [showPalChooser, setShowPalChooser] = useState(false);
  const [palPlatform, setPalPlatform] = useState(null);
  
  // Timer state for mode-based countdown
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Group Quiz Mode timer states
  const [showStartTimerButton, setShowStartTimerButton] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [quizCountdown, setQuizCountdown] = useState(0);
  
  // NEW: Zero-Timer Mode states
  const [currentScreen, setCurrentScreen] = useState(1);
  const [zeroTimerMode, setZeroTimerMode] = useState(false);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [shouldShowInlineAd, setShouldShowInlineAd] = useState(false);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  
  // 🆕 Track completed grids for picture rotation
  const [completedGridCount, setCompletedGridCount] = useState(0);
  
  // 🎉 MILESTONE CELEBRATION STATE (2025.11.27.1700)
  const [showMilestoneCelebration, setShowMilestoneCelebration] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [claimedMilestones, setClaimedMilestones] = useState([]);
  
  // 🔍 DEBUG: Log whenever completedGridCount changes
  useEffect(() => {
  }, [completedGridCount]);
  
  // 🎉 LOAD CLAIMED MILESTONES FROM PLAYER DATA (2025.11.27.1700)
  useEffect(() => {
    if (playerData?.stats?.claimedMilestones) {
      setClaimedMilestones(playerData.stats.claimedMilestones);
    }
  }, [playerData?.stats?.claimedMilestones]);
  
  // 🎉 MILESTONE CHECK - Triggers when totalTraks changes (2025.11.27.1700)
  // 🔧 FIX 2025.12.04: Initialize to null to detect first render, skip milestone check on game start
  const [previousTotalTraks, setPreviousTotalTraks] = useState(null);
  
  useEffect(() => {
    // Calculate actual total (session + saved)
    const actualTotal = (playerData?.stats?.totalTraks || 0) + traksInGame;
    
    // 🔧 FIX 2025.12.04: Skip milestone check on initial load - just set baseline
    if (previousTotalTraks === null) {
      console.log('🏆 MILESTONE: Game start, setting baseline TRAKs:', actualTotal);
      setPreviousTotalTraks(actualTotal);
      return;
    }
    
    // 🔧 FIX 2025.12.04: Only check if TRAKs actually INCREASED from gameplay
    if (actualTotal <= previousTotalTraks) {
      setPreviousTotalTraks(actualTotal);
      return;
    }
    
    // Check if we crossed a milestone threshold (skip in Group Quiz mode)
    if (!groupQuizMode) {
      console.log('🏆 MILESTONE: TRAKs increased from', previousTotalTraks, 'to', actualTotal);
      const milestone = checkMilestoneReached(previousTotalTraks, actualTotal, claimedMilestones);
      
      if (milestone) {
        // 🔧 FIX 2025.12.13: Use async IIFE for database call
        (async () => {
          console.log('🏆 MILESTONE UNLOCKED:', milestone.name, 'at', milestone.traks, 'TRAKs');
        
          // 🔧 FIX 2025.12.13: Check if already claimed in database BEFORE creating reward
          const token = getToken();
          let shouldCreateReward = true;
          
          if (token) {
            try {
              const response = await fetch(`${API_URL}/profile/claim-milestone`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ milestone_traks: milestone.traks })
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.already_claimed) {
                  console.log(`⚠️ Milestone ${milestone.name} already claimed in database, skipping reward creation`);
                  shouldCreateReward = false;
                } else {
                  console.log(`✅ Milestone ${milestone.name} marked as claimed in database`);
                }
              }
            } catch (err) {
              console.error('❌ Error checking milestone claim status:', err);
            }
          }
          
          // Only create reward if not already claimed
          if (shouldCreateReward) {
            // Trigger celebration
            setCurrentMilestone(milestone);
            setShowMilestoneCelebration(true);
            
            // 🔧 FIX 2025.12.04: DON'T add bonus tags here - App.js handles saving to playerData
            // The bonus tags will be added to playerData.stats.totalTags when player exits
            // This prevents double-counting and save timing issues
            
            // Log achievement reward to database
            logAchievementReward(milestone.name, milestone.traks, milestone.bonusTraks);
            console.log(`🎁 Created reward for ${milestone.name}: +${milestone.bonusTraks} TRAKs`);
            
            // Auto-dismiss after 5 seconds (extended for readability)
            setTimeout(() => {
              setShowMilestoneCelebration(false);
              setCurrentMilestone(null);
            }, 5000);
          }
          
          // Mark milestone as claimed locally (even if already in DB, prevents local re-trigger)
          setClaimedMilestones(prev => [...prev, milestone.traks]);
        })();
      }
    }
    
    setPreviousTotalTraks(actualTotal);
  }, [traksInGame, playerData?.stats?.totalTraks]);

  const handleSendContact = async () => {
    if (!contactSubject.trim() || !contactMessage.trim()) {
      alert('Please fill in subject and message');
      return;
    }
    
    setContactSending(true);
    
    try {
      const response = await fetch('https://api.traktag.com/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: playerData?.name || 'Anonymous',
          playerId: playerData?.player_id || 'unknown',
          game: selectedGame?.name || 'Unknown Game',
          subject: contactSubject,
          message: contactMessage
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setContactSent(true);
        setTimeout(() => {
          setShowContactForm(false);
          setContactSubject('');
          setContactMessage('');
          setContactSent(false);
        }, 2000);
      } else {
        alert('Failed to send message: ' + result.message);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setContactSending(false);
    }
  };
  
  // Detect zero-timer mode
  useEffect(() => {
    setZeroTimerMode(groupQuizMode && groupQuizTimer === 0);
    if (groupQuizMode && groupQuizTimer === 0) {
      setCurrentScreen(1); // Start at Screen 1
    }
  }, [groupQuizMode, groupQuizTimer]);
  
  const victoryMessages = [
    "You showed that question who's boss!",
    "Nailed it! Perfect score.",
    "Boom! That's how it's done."
  ];
  
  useEffect(() => {
    shuffleAnswers();
  }, [currentQuestionIndex, groupQuizPosition, groupQuizMode]);
  
  // ============================================
  // 🔧 FIX 2026.01.08.1600: CLEAN GRID STATE MACHINE
  // Grid shows on TWO events only:
  // 1. Initial mount (via useState initialization)
  // 2. Question change (via this useEffect)
  // ============================================
  
  // Mark quiz as started on mount
  useEffect(() => {
    if (!quizStarted) {
      console.log('🎮 QUIZ STARTING (v2026.01.08.2000)');
      setQuizStarted(true);
      setIsProcessingAnswer(false);
    }
  }, [quizStarted]);
  
  // Track previous question index to detect actual question changes
  const prevQuestionIndexRef = React.useRef(currentQuestionIndex);
  
  // Show grid when moving to NEXT question (not on mount - that's handled by useState)
  useEffect(() => {
    // Skip if this is the initial render (prevRef matches current)
    if (prevQuestionIndexRef.current === currentQuestionIndex) {
      return;
    }
    
    // Update ref for next comparison
    prevQuestionIndexRef.current = currentQuestionIndex;
    
    // Don't show grid in group quiz mode
    if (groupQuizMode) return;
    
    // Check if grid is enabled
    const savedGridSetting = localStorage.getItem('pictureGridEnabled');
    const pictureGridEnabled = savedGridSetting === null ? true : savedGridSetting === 'true';
    
    if (pictureGridEnabled) {
      console.log('🎮 QUESTION CHANGED → Showing grid for question', currentQuestionIndex);
      setShowPictureGrid(true);
      setWaitingForGridComplete(true);
      setShowQuestion(false);
      setShowAnswer(false);
      setSelectedAnswer(null);
      setShowChallengePopup(false);
    }
  }, [currentQuestionIndex, groupQuizMode]);
  
  // ============================================
  // ✏️ FORCE HIDE GRID if toggle is turned OFF
  // 🔧 FIX 2025.12.03: Default to TRUE if never set
  // ============================================
  useEffect(() => {
    const savedGridSetting = localStorage.getItem('pictureGridEnabled');
    const pictureGridEnabled = savedGridSetting === null ? true : savedGridSetting === 'true';
    if (!pictureGridEnabled && showPictureGrid) {
      setShowPictureGrid(false);
      setWaitingForGridComplete(false);
      setShowQuestion(true); // ← Show question instead!
    }
  }, [showPictureGrid]);
  
  // Picture Grid Handlers
  const handleGridCellTap = (cellIndex) => {
    if (gridTappedCells.has(cellIndex)) return;
    
    const newTapped = new Set(gridTappedCells);
    newTapped.add(cellIndex);
    setGridTappedCells(newTapped);
    
    // Check if all cells are tapped
    if (newTapped.size >= 25) {
      // Reset grid and move to next picture
      setTimeout(() => {
        setGridTappedCells(new Set());
        const nextIndex = (currentPictureIndex + 1) % Math.max(1, pictureGridSettings.pictureUrls.length);
        setCurrentPictureIndex(nextIndex);
      }, 500);
    }
  };
  
  // Message handling removed - using simple email contact instead
  
  // 🔧 FIX 2025.11.27.1615: Added isLastCell parameter - only increment grid count on FULL grid completion
  const handleGridComplete = (gridTraks = 0, isLastCell = false) => {
    console.log('🎯 handleGridComplete CALLED:', { gridTraks, isLastCell, currentTraksInGame: traksInGame });
    
    // Add grid TRAKs to game TRAKs
    if (gridTraks > 0) {
      console.log('💰 ADDING GRID TRAKs:', gridTraks, '→ traksInGame will be:', traksInGame + gridTraks);
      setTraksInGame(prev => {
        const newTotal = prev + gridTraks;
        console.log('💰 traksInGame updated:', prev, '→', newTotal);
        return newTotal;
      });
      setTotalTraks(prev => prev + gridTraks);
      
      // 🔧 FIX 2025.12.04: Log grid bonus reward to database
      logGridBonusReward(
        gridTraks, 
        selectedGameId, 
        selectedGame?.name || 'Quiz',
        gridTraks >= 50  // isSpecialBonus if 50+ TRAKs
      );
    } else {
      console.log('⚠️ gridTraks is 0 or undefined - no TRAKs added');
    }
    
    // 🔧 FIX 2025.11.27.1715: Only increment grid counter when FULL 16-cell grid is completed
    // This ensures picture only rotates after completing entire grid, not after each cell
    if (isLastCell) {
      setCompletedGridCount(prev => {
        const newCount = prev + 1;
        return newCount;
      });
    } else {
    }
    
    // 🔧 FIX 2025.11.27.1810: Reset ALL relevant states BEFORE hiding grid
    // Order matters for React batching - set question state first
    setShowAnswer(false);
    setSelectedAnswer(null);
    setIsProcessingAnswer(false);
    setShowChallengePopup(false);
    
    // 🔧 FIX 2026.01.08.1600: Simply transition - no ref needed
    // Grid won't re-trigger because useEffect only watches currentQuestionIndex
    setWaitingForGridComplete(false);
    
    // Now transition from grid to question
    console.log('🎮 GRID COMPLETE → Showing question (v2026.01.08.2000)');
    setShowQuestion(true);
    setShowPictureGrid(false);
    
  };
  
  // Calculate if the current answer is correct (needed for useEffect below)
  const isCorrect = selectedAnswer === correctAnswerIndex;
  
  // ============================================
  // ✏️ Dynamic Question Text Sizing
  // Keeps question box fixed height by shrinking text for long questions
  // ============================================
  const getQuestionTextSize = (questionText) => {
    // 🔧 SAFETY CHECK: Handle undefined or null questionText
    if (!questionText || typeof questionText !== 'string') {
      return 'text-lg'; // Default size if no valid text
    }
    const length = questionText.length;
    if (length < 80) return 'text-xl';      // Short questions - large text
    if (length < 150) return 'text-lg';     // Medium questions
    if (length < 250) return 'text-base';   // Long questions
    return 'text-sm';                        // Very long questions - smallest
  };
  
  // ============================================
  // ✏️ AUTO-ADVANCE DISABLED - Using swipe gestures instead
  // Users now swipe left to advance to next question
  // ============================================
  // Auto-continue after correct answer or victory
  // useEffect(() => {
  //   if (((showAnswer && isCorrect && !isRetry && !groupQuizMode && !viewOnlyAnswer) || showVictory) && !hoveringButton && !showChallengePopup) {
  //     const timer = setTimeout(() => {
  //       handleNextQuestion();
  //     }, 3000);
  //     
  //     return () => clearTimeout(timer);
  //   }
  // }, [showAnswer, isCorrect, isRetry, showVictory, groupQuizMode, viewOnlyAnswer, hoveringButton, showChallengePopup]);
  
  // 🔧 FIX 2025.11.28: Delay showing End Game/Challenge buttons to prevent accidental taps
  // Buttons appear 1.5 seconds after answer is shown
  useEffect(() => {
    if (showAnswer) {
      // Hide buttons immediately when answer shows
      setShowActionButtons(false);
      
      // Show buttons after 1.5 second delay
      const timer = setTimeout(() => {
        setShowActionButtons(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    } else {
      // Reset when answer is hidden
      setShowActionButtons(false);
    }
  }, [showAnswer]);
  
  // 🆕 Exit Ad countdown effect
  useEffect(() => {
    if (showExitAd && exitAdCountdown > 0) {
      const timer = setTimeout(() => {
        setExitAdCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showExitAd, exitAdCountdown]);
  
  // 🆕 Check if Exit Ad should show (date range & impressions)
  const shouldShowExitAd = () => {
    if (!selectedGame?.exitAdUrl) return false;
    
    const today = new Date().toISOString().split('T')[0];
    
    // Check date range
    if (selectedGame.exitAdStartDate && today < selectedGame.exitAdStartDate) return false;
    if (selectedGame.exitAdEndDate && today > selectedGame.exitAdEndDate) return false;
    
    // Check impressions limit
    if (selectedGame.exitAdMaxImpressions) {
      const currentImpressions = selectedGame.exitAdImpressions || 0;
      if (currentImpressions >= selectedGame.exitAdMaxImpressions) return false;
    }
    
    return true;
  };
  
  // 🆕 Handle Exit Game - show ad first if configured
  const handleExitWithAd = () => {
    if (shouldShowExitAd()) {
      // Show Exit Ad
      setShowExitAd(true);
      setExitAdCountdown(selectedGame.exitAdCountdown || 5);
      
      // Increment impressions (would save to database in production)
      // For now, update localStorage
      const games = JSON.parse(localStorage.getItem('games') || '[]');
      const updatedGames = games.map(g => {
        if (g.game_id === selectedGame.game_id) {
          return { ...g, exitAdImpressions: (g.exitAdImpressions || 0) + 1 };
        }
        return g;
      });
      localStorage.setItem('games', JSON.stringify(updatedGames));
    } else {
      // No ad - exit directly with session stats
      if (onExitGame) onExitGame({
        traksEarned: traksInGame,
        tagsEarned: tagsInGame,
        questionsAnswered: attemptedInGame,
        correctAnswers: correctInGame
      });
    }
  };
  
  // 🆕 Close Exit Ad and go home
  const handleCloseExitAd = () => {
    setShowExitAd(false);
    setGameEnded(false);
    if (onExitGame) onExitGame({
      traksEarned: traksInGame,
      tagsEarned: tagsInGame,
      questionsAnswered: attemptedInGame,
      correctAnswers: correctInGame
    });
  };
  
  // Timer countdown effect for timed modes
  // 🔧 FIX 2025.12.14: Replaced over-complicated timer with simple Dec 2 version
  useEffect(() => {
    // Only run timer if in timed mode and question is showing (not in group quiz)
    if (showQuestion && !showAnswer && modeConfig[safeCurrentMode].timer && !groupQuizMode) {
      // Initialize timer
      setTimeRemaining(modeConfig[safeCurrentMode].timer);
      
      // Start countdown
      const timerInterval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 0) {
            clearInterval(timerInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Cleanup on unmount or when conditions change
      return () => clearInterval(timerInterval);
    } else if (!modeConfig[safeCurrentMode].timer) {
      // Reset timer for non-timed modes
      setTimeRemaining(null);
    }
  }, [showQuestion, showAnswer, currentQuestionIndex, currentMode, groupQuizMode]);
  
  // Handle timer expiration - 2025.12.23: Now shows Time Out screen first
  useEffect(() => {
    if (timeRemaining === 0 && showQuestion && !showAnswer && selectedAnswer === null && !groupQuizMode && !showTimeOutScreen) {
      // Time's up - show Time Out screen first (with speed adjustment option)
      setShowTimeoutMessage(true);
      
      // Scroll to top immediately so user sees the message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Process timeout after showing message (1.5 seconds) then show Time Out screen
      setTimeout(() => {
        setShowTimeoutMessage(false);
        
        // Lose TRAK for timeout
        const lostTraks = traksInGame;
        setTotalTraks(prev => prev - lostTraks);
        setSavedTraks(traksInGame); // Save TRAKs so they can be recovered with Watch Ad
        setTraksInGame(0);
        
        // Update stats
        setAttemptedInGame(prev => prev + 1);
        
        // Update mode statistics (incorrect - timeout counts as wrong)
        setModeStats(prev => ({
          ...prev,
          [currentMode]: {
            questions: prev[currentMode].questions + 1,
            correct: prev[currentMode].correct
          }
        }));
        
        // Save last attempted question for challenge reports
        const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
        const currentQ = questionDatabase[questionIndex];
        setLastAttemptedQuestion({
          questionId: currentQ.question_id || currentQ.questionId || questionIndex,
          questionText: currentQ.question_text,
          correctAnswer: currentQ.correct_answer,
          allAnswers: shuffledAnswers,
          playerAnswer: "⏰ Time's up!",
          wasCorrect: false
        });
        
        // 🆕 Show Time Out screen instead of going directly to Brain Glitch
        setShowTimeOutScreen(true);
      }, 1500);
    }
  }, [timeRemaining]);
  
  // 🆕 Handle Continue from Time Out screen -> Brain Glitch screen
  const handleTimeOutContinue = () => {
    setShowTimeOutScreen(false);
    setShowQuestion(false);
    setShowAnswer(true);
    setSelectedAnswer(-1); // -1 indicates timeout (isCorrect will be false)
  };
  
  // 🆕 Handle speed mode change
  const handleSpeedModeChange = (modeId) => {
    setSelectedSpeedMode(modeId);
    localStorage.setItem('preferredSpeedMode', modeId);
    // Note: This will take effect for the next question, not the current one
  };
  
  // Group Quiz Mode: Initialize timer button state when mode is toggled
  useEffect(() => {
    if (groupQuizMode && showQuestion && !showAnswer) {
      // Always show button in Group Quiz Mode (whether timer or not)
      setShowStartTimerButton(true);
    } else {
      setShowStartTimerButton(false); // Not in Group Quiz mode
    }
  }, [groupQuizMode, groupQuizTimer, showQuestion, showAnswer, groupQuizPosition]);
  
  // FIX BUG #1: Reset state when Group Quiz Mode is toggled ON or OFF
  useEffect(() => {
    if (groupQuizMode) {
      // 🔧 FIX: When turning ON Group Quiz mode, reset to show question
      setShowStartTimerButton(true);
      setTimerActive(false);
      setQuizCountdown(0);
      setShowQuestion(true);   // ← CRITICAL FIX!
      setShowAnswer(false);    // ← CRITICAL FIX!
      setSelectedAnswer(null);
      setQuestionsInSession(0);
      setShowInterstitialAd(false);
      setInterstitialAdCountdown(0);
      
      // For zero-timer mode, start at Screen 1
      if (groupQuizTimer === 0) {
        setCurrentScreen(1);
      }
    } else {
      // When turning OFF Group Quiz mode
      setShowStartTimerButton(false);
      setTimerActive(false);
      setQuizCountdown(0);
      setShowQuestion(true);
      setShowAnswer(false);
      setSelectedAnswer(null);
      setQuestionsInSession(0);
      setShowInterstitialAd(false);
      setInterstitialAdCountdown(0);
    }
  }, [groupQuizMode, groupQuizTimer]);
  
  
  const shuffleAnswers = () => {
    // Use the appropriate question index based on mode
    const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
    const currentQ = questionDatabase[questionIndex];
    
    // 🔧 SAFETY CHECK: Ensure currentQ exists
    if (!currentQ) {
      console.error('❌ No question found at index', questionIndex, 'in database of', questionDatabase.length, 'questions');
      // Set default empty answers to prevent crash
      setShuffledAnswers(['No answer', 'available', 'for', 'question']);
      setCorrectAnswerIndex(0);
      return;
    }
    
    // 🔧 STANDARDIZED: Use database format (question_text, correct_answer, wrong_answer1/2/3)
    const wrongAnswersList = [
      currentQ.wrong_answer1,
      currentQ.wrong_answer2,
      currentQ.wrong_answer3
    ].filter(a => {
      if (!a) return false;
      const val = a.toLowerCase().trim();
      // Filter out N/A variants and empty values
      const shouldFilter = ['n/a', 'na', 'n\\a', '-', '', 'none', 'null', 'undefined'].includes(val) || val.startsWith('n/a');
      return !shouldFilter;
    }); // Remove undefined/null/N/A values  
    const correctAns = currentQ.correct_answer || "";
    const allAnswers = [correctAns, ...wrongAnswersList];
    const shuffled = [...allAnswers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledAnswers(shuffled);
    setCorrectAnswerIndex(shuffled.indexOf(correctAns));
  };
  
  // ============================================
  // 🎮 SWIPE GESTURE HANDLERS
  // Minimum swipe distance: 50px
  // Direction: Left only (natural "next" motion)
  // ============================================
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe) {
      // Check if we're on a swipeable screen
      if (showVictory || (viewOnlyAnswer && showAnswer)) {
        handleNextQuestion();
      }
    }
  };
  
  const handleAnswerClick = (index) => {
    // ============================================
    // ✏️ CLICK GUARD: Prevent double-click and rapid clicking
    // ============================================
    if (selectedAnswer !== null || isProcessingAnswer) {
      return;
    }
    setIsProcessingAnswer(true);
    
    // Use the appropriate question index based on mode
    const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
    const currentQ = questionDatabase[questionIndex];
    
    // 🔧 SAFETY CHECK: Ensure currentQ exists
    if (!currentQ) {
      console.error('❌ No question found at index', questionIndex);
      setIsProcessingAnswer(false);
      alert('Error: Question not found. Please select a different game.');
      return;
    }
    
    setSelectedAnswer(index);
    
    // Handle timeout case (index === -1)
    const isTimeout = index === -1;
    const correct = !isTimeout && index === correctAnswerIndex;
    
    // Save last attempted question for challenge reports
    setLastAttemptedQuestion({
      questionId: currentQ.question_id || currentQ.questionId || questionIndex,
      questionText: currentQ.question_text,
      correctAnswer: currentQ.correct_answer,
      allAnswers: shuffledAnswers,
      playerAnswer: isTimeout ? "⏰ Time's up!" : shuffledAnswers[index],
      wasCorrect: correct
    });
    
    setShowQuestion(false);
    setShowAnswer(true);
    setAttemptedInGame(prev => prev + 1);
    
    
    // Skip scoring in Group Quiz mode
    if (groupQuizMode) {
      // Track question history for summary - ONLY correct answer
      setQuestionHistory(prev => [...prev, {
        questionText: currentQ.question_text,
        correctAnswer: currentQ.correct_answer
      }]);
      setIsProcessingAnswer(false); // ✏️ FIX: Reset before returning
      return; // Just show correct/wrong, no scoring
    }
    if (isRetry) {
      if (correct) {
        const restoredTraks = savedTraks + 1;
        
        // Halve TRAK if Ask a Pal was used
        let finalTraks = restoredTraks;
        if (palUsed) {
          finalTraks = Math.floor(savedTraks * 0.5) + 1;
        }
        
        setTraksInGame(finalTraks);
        setTotalTraks(prev => prev + finalTraks);
        const randomMsg = victoryMessages[Math.floor(Math.random() * victoryMessages.length)];
        setVictoryMessage(randomMsg);
        setShowVictory(true);
        setSavedTraks(0);
      } else {
        const lostTraks = traksInGame;
        setTotalTraks(prev => prev - lostTraks);
        // Only save TRAK if we're losing some (don't overwrite on retry failures)
        if (traksInGame > 0) {
          setSavedTraks(traksInGame);
        }
        setTraksInGame(0);
      }
    } else {
      if (correct) {
        setCorrectInGame(prev => prev + 1);
        
        // Calculate TRAK with Ask a Pal
        let finalTraks;
        if (palUsed) {
          // savedTraks already has 50% deducted when clicking share button
          // So just restore savedTraks + 1 for getting it right
          // Example: Had 6, paid 3 (50%), savedTraks=3, get back 3+1=4
          finalTraks = savedTraks + 1;
        } else {
          // Normal: just add 1
          finalTraks = traksInGame + 1;
        }
        
        const trakChange = finalTraks - traksInGame; // Net change to apply
        setTraksInGame(finalTraks);
        setTotalTraks(prev => prev + trakChange);
        
        // Tags are NOT reduced for Ask a Pal - only TRAKs are halved
        const baseTags = currentQ.difficulty_points || currentQ.tag || currentQ.tag_value || 10;  // No Ask a Pal penalty on tags - only TRAKs are halved
        const tagsEarned = Math.floor(baseTags * modeConfig[safeCurrentMode].tagsMultiplier);
        setTagsInGame(prev => prev + tagsEarned);
        
        // 🔧 FIX 2025.12.04: Log correct answer reward to database
        logCorrectAnswerReward(
          selectedGameId, 
          selectedGame?.name || 'Quiz',
          currentQ.question_id || currentQ.questionId
        );
        
        // Update mode statistics (correct answer)
        setModeStats(prev => ({
          ...prev,
          [currentMode]: {
            questions: prev[currentMode].questions + 1,
            correct: prev[currentMode].correct + 1
          }
        }));
      } else {
        const lostTraks = traksInGame;
        setTotalTraks(prev => prev - lostTraks);
        
        // If Ask a Pal was used, save the halved amount (TRAKs / 2 + 1)
        if (palUsed) {
          const halvedSave = Math.floor(traksInGame * 0.5) + 1;
          setSavedTraks(halvedSave);
        } else {
          setSavedTraks(traksInGame);
        }
        setTraksInGame(0);
        
        // Update mode statistics (incorrect answer)
        setModeStats(prev => ({
          ...prev,
          [currentMode]: {
            questions: prev[currentMode].questions + 1,
            correct: prev[currentMode].correct
          }
        }));
      }
    }
  };
  
  const handleWatchAd = () => {
    setWatchingAd(true);
    setAdCountdown(5);
    
    // Track when reward ad is shown for interstitial ad logic
    setLastRewardAdPlayCount(playCount);
    const countdown = setInterval(() => {
      setAdCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          retryQuestion();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const retryQuestion = () => {
    setWatchingAd(false);
    setSelectedAnswer(null);
    setShowQuestion(true);
    setShowAnswer(false);
    setIsRetry(true);
    setPalUnlocked(true); // Unlock Ask a Pal feature after watching ad
    setIsProcessingAnswer(false); // ✏️ CRITICAL: Reset processing flag
    setPalUsed(false);              // 🔧 BUG FIX: Clear Ask a Pal state
    setPalPlatform(null);           // 🔧 BUG FIX: Clear platform choice
    shuffleAnswers();
  };
  
  // 🆕 2026.01.08: Buy back retry using TRAKs as currency
  // 🔧 FIX 2026.01.08.2000: Cost is 25% of savedTraks (not 100%)
  const handleBuyBackRetry = () => {
    if (savedTraks <= 0) {
      console.log('❌ No TRAKs to spend on buy back');
      return;
    }
    
    // Calculate 25% cost
    const buyBackCost = Math.floor(savedTraks * 0.25);
    const remainingTraks = savedTraks - buyBackCost;
    
    console.log(`💎 BUY BACK: Cost ${buyBackCost} TRAKs (25% of ${savedTraks})`);
    console.log(`📊 TRAKs after buy back: ${remainingTraks}`);
    
    // Deduct 25% fee, keep 75% for potential recovery
    // If they get it right on retry, they get remainingTraks + 1
    setSavedTraks(remainingTraks);
    
    // Trigger retry
    setSelectedAnswer(null);
    setShowQuestion(true);
    setShowAnswer(false);
    setIsRetry(true);
    setPalUnlocked(false); // Don't unlock Ask a Pal (didn't watch ad)
    setIsProcessingAnswer(false);
    setPalUsed(false);
    setPalPlatform(null);
    shuffleAnswers();
  };
  
  const handleNextQuestion = () => {
    
    // 🆕 REGISTRATION CHECK: After 16 questions, prompt anonymous players to register
    if (!isLoggedIn) {
      const totalQuestionsPlayed = parseInt(localStorage.getItem('anonymousQuestionsPlayed') || '0', 10) + 1;
      localStorage.setItem('anonymousQuestionsPlayed', totalQuestionsPlayed.toString());
      
      if (totalQuestionsPlayed >= 17) {
        setShowRegistrationPrompt(true);
        return; // Don't load next question until registered
      }
    }
    
    // Increment play count for interstitial ad tracking
    const newPlayCount = playCount + 1;
    setPlayCount(newPlayCount);
    
    // Check if interstitial ad should be shown
    let shouldShowInterstitial = false;
    
    if (lastRewardAdPlayCount !== null && newPlayCount === lastRewardAdPlayCount + 8) {
      // Show interstitial 8 plays after reward ad
      shouldShowInterstitial = true;
      setLastRewardAdPlayCount(null); // Reset to go back to 26-play schedule
    } else if (newPlayCount % 26 === 0) {
      // Show interstitial every 26 plays (if no recent reward ad)
      shouldShowInterstitial = true;
    }
    
    if (shouldShowInterstitial) {
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show interstitial ad
      setShowInterstitialAd(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowInterstitialAd(false);
      }, 3000);
    }
    
    setSelectedAnswer(null);
    setShowQuestion(true);
    setShowAnswer(false);
    setIsRetry(false);
    setShowVictory(false);
    setPalUnlocked(false); // Reset pal unlock for new question
    setPalUsed(false); // Reset pal usage for new question
    setPalPlatform(null); // Reset platform choice
    setWaitingForGridComplete(false); // Reset grid wait state
    setViewOnlyAnswer(false); // ✏️ Reset view-only mode
    setIsProcessingAnswer(false); // ✏️ Reset answer processing guard
    
    // Advance to next question based on mode
    if (groupQuizMode) {
      // 🔧 FIX 2025.11.29: Group Quiz Mode goes BACKWARDS (decrement)
      // When reaches 0, wraps to END (last question)
      const totalQuestions = questionDatabase.length;
      
      // 🛡️ CRASH GUARD: Handle empty database
      if (totalQuestions === 0) {
        console.error('❌ CRASH GUARD: No questions in database!');
        return; // Don't advance, stay put
      }
      
      let nextPosition;
      if (groupQuizPosition <= 0) {
        // Wrap to END (last valid index)
        nextPosition = totalQuestions - 1;
      } else {
        // Decrement (go backwards)
        nextPosition = groupQuizPosition - 1;
      }
      
      // 🛡️ CRASH GUARD: Ensure position is always valid
      if (nextPosition < 0) nextPosition = 0;
      if (nextPosition >= totalQuestions) nextPosition = totalQuestions - 1;
      
      setGroupQuizPosition(nextPosition);
      
      // 🔧 FIX 2025.12.14.2215: Removed duplicate history tracking here
      // History is already tracked when timer completes in handleStartTimer (line ~1601)
      
      // 🔧 FIX: Reset ALL timer states for next question
      setShowStartTimerButton(true);
      setTimerActive(false);  // Ensure timer isn't active
      setQuizCountdown(0);    // Reset countdown
      setShowInterstitialAd(false); // Hide any ads
      
      
      // For zero-timer mode, set to Screen 4 (Next Question)
      if (zeroTimerMode) {
        setCurrentScreen(4);
      }
    } else {
      // 🔧 FIX 2025.11.29: Player Mode goes FORWARDS (increment) via playerQuizPosition
      // When reaches END, wraps to 0
      const totalQuestions = questionDatabase.length;
      
      // 🛡️ CRASH GUARD: Handle empty database
      if (totalQuestions === 0) {
        console.error('❌ CRASH GUARD: No questions in database!');
        return; // Don't advance, stay put
      }
      
      let nextPosition;
      if (playerQuizPosition >= totalQuestions - 1) {
        // Wrap to BEGINNING
        nextPosition = 0;
      } else {
        // Increment (go forwards)
        nextPosition = playerQuizPosition + 1;
      }
      
      // 🛡️ CRASH GUARD: Ensure position is always valid
      if (nextPosition < 0) nextPosition = 0;
      if (nextPosition >= totalQuestions) nextPosition = totalQuestions - 1;
      
      setPlayerQuizPosition(nextPosition);
      setCurrentQuestionIndex(nextPosition);
      
      // Check if picture grid should show for Player Mode
      // 🔧 FIX 2025.12.03: Default to TRUE if never set
      const savedGridSetting = localStorage.getItem('pictureGridEnabled');
      const gridEnabled = savedGridSetting === null ? true : savedGridSetting === 'true';
      if (gridEnabled) {
        setShowPictureGrid(true);
        setWaitingForGridComplete(true);
        setShowQuestion(false);
        setShowAnswer(false);  // 🔧 FIX 2025.11.27.1815: Reset answer state!
        setSelectedAnswer(null); // 🔧 FIX: Clear any previous selection
      }
    }
    
    // Reset Group Quiz timer states
    setTimerActive(false);
    setQuizCountdown(0);
  };
  
  const handleSkipToNew = () => {
    setSavedTraks(0);
    
    // ============================================
    // ✏️ Show correct answer in VIEW-ONLY mode
    // No points, no celebration, just show the answer
    // ============================================
    setShowQuestion(false);
    setShowAnswer(true);
    setSelectedAnswer(correctAnswerIndex);
    setViewOnlyAnswer(true); // Mark as view-only
    
    // User will manually continue from answer screen
  };
  
  const handleAdClick = () => {
    
    // Only works for untimed mode (groupQuizTimer === 0)
    if (groupQuizTimer !== 0) {
      return;
    }
    
    
    const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
    const currentQ = questionDatabase[questionIndex];
    
    console.log('📝 Question:', currentQ.question_text);
    
    // Close ad and show answer
    setShowInterstitialAd(false);
    setShowQuestion(false);
    setShowAnswer(true);
    setSelectedAnswer(correctAnswerIndex);
    
    console.log('✅ States updated - ad should close now');
    
    // Set lastAttemptedQuestion for Challenge button
    setLastAttemptedQuestion({
      questionId: currentQ.question_id || currentQ.questionId || questionIndex,
      questionText: currentQ.question_text,
      correctAnswer: currentQ.correct_answer,
      allAnswers: shuffledAnswers,
      playerAnswer: "No timer - answer revealed",
      wasCorrect: true
    });
    
    // Track in history
    setQuestionHistory(prev => [...prev, {
      questionText: currentQ.question_text,
      correctAnswer: currentQ.correct_answer
    }]);
  };
  
  // NEW: Zero-Timer Mode Screen Click Handler
  const handleScreenClick = () => {
    if (!zeroTimerMode) return;
    
    // 🔧 FIX: Prevent double-clicks
    if (isProcessingAnswer) {
      console.log('🚫 Click ignored - already processing');
      return;
    }
    
    setIsProcessingAnswer(true);
    
    
    switch(currentScreen) {
      case 1: // Waiting → Question
        setCurrentScreen(2);
        setShowQuestion(true);
        setShowAnswer(false);
        
        // Check if ad should show for Screen 2
        const firstQuestionCount = questionsInSession + 1;
        setQuestionsInSession(firstQuestionCount);
        const firstAdFrequency = selectedGame?.groupQuizAdFrequency || 5;
        const firstShouldShowAd = firstAdFrequency > 0 && (firstQuestionCount % firstAdFrequency === 0);
        setShouldShowInlineAd(firstShouldShowAd);
        
        // Reset processing guard after state updates
        setTimeout(() => setIsProcessingAnswer(false), 100);
        break;
        
      case 2: // Question → Answer
        // Go directly to answer - keep the same ad state from Screen 2
        setCurrentScreen(3);
        setShowAnswer(true);
        setShowQuestion(false);
        setSelectedAnswer(correctAnswerIndex);
        
        // Set lastAttemptedQuestion for Challenge button
        setLastAttemptedQuestion({
          questionId: groupQuizPosition,
          questionText: currentQ.question_text,
          correctAnswer: currentQ.correct_answer,
          allAnswers: shuffledAnswers,
          playerAnswer: "Group Quiz Mode",
          wasCorrect: true
        });
        
        // Track in history
        setSessionQuestions(prev => [...prev, {
          questionText: currentQ.question_text,
          correctAnswer: currentQ.correct_answer
        }]);
        
        // Keep the same ad state - if ad was showing on Screen 2, show on Screen 3
        // shouldShowInlineAd remains unchanged
        
        // Reset processing guard
        setTimeout(() => setIsProcessingAnswer(false), 100);
        break;
        
      case 3: // Answer → Next Question
        // 🔧 FIX 2025.11.29: Move BACKWARDS (decrement) with wrap to END
        const totalQs = questionDatabase.length;
        let nextPos;
        if (groupQuizPosition <= 0) {
          nextPos = totalQs - 1; // Wrap to END
        } else {
          nextPos = groupQuizPosition - 1; // Decrement
        }
        setGroupQuizPosition(nextPos);
        
        // 🔧 FIX: Go directly to Screen 2 (skip Screen 4)
        setCurrentScreen(2);
        setShowQuestion(true);
        setShowAnswer(false);
        setSelectedAnswer(null);
        
        // Check if ad should show for next question
        const nextQuestionCount = questionsInSession + 1;
        setQuestionsInSession(nextQuestionCount);
        const nextAdFrequency = selectedGame?.groupQuizAdFrequency || 5;
        const nextShouldShowAd = nextAdFrequency > 0 && (nextQuestionCount % nextAdFrequency === 0);
        setShouldShowInlineAd(nextShouldShowAd);
        
        // Reset processing guard
        setTimeout(() => setIsProcessingAnswer(false), 100);
        break;
        
      default:
        console.warn('❌ Unknown screen:', currentScreen);
        setTimeout(() => setIsProcessingAnswer(false), 100);
    }
  };
  
  // NEW: Clickable Green Box Component
  const ClickableGreenBox = ({ text, onClick }) => (
    <div 
      className="bg-orange-600 hover:bg-orange-500 rounded-lg py-3 px-4 text-center cursor-pointer mb-2"
      onClick={onClick}
    >
      <span className="text-white text-xl font-bold">{text}</span>
    </div>
  );
  
  // NEW: Answer Display Component
  const AnswerDisplay = ({ answer }) => (
    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg py-4 px-4 text-center mb-2">
      <p className="text-yellow-900 text-base font-bold mb-1">✅ CORRECT ANSWER:</p>
      <p className="text-yellow-900 text-2xl font-black">{answer}</p>
    </div>
  );
  
  // 📺 Static Ad Component - Displays during quiz
  const StaticAd = () => {
    if (!selectedGame?.staticAdUrl) return null;
    
    return (
      <div className="my-3">
        <p className="text-gray-400 text-xs text-center mb-1">Advertisement</p>
        <a 
          href={selectedGame.adClickUrl || 'https://traktag.com'} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block"
        >
          <img 
            src={selectedGame.staticAdUrl} 
            alt="Advertisement"
            className="mx-auto rounded border border-gray-700"
            style={{maxWidth: '300px', maxHeight: '250px', width: '100%', height: 'auto'}}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </a>
      </div>
    );
  };
  
  // Handle end quiz for review
  const handleReviewQuestions = () => {
    setQuizEnded(true);
  };
  
  const handleStartTimer = () => {
    // Increment question counter
    const newQuestionCount = questionsInSession + 1;
    setQuestionsInSession(newQuestionCount);
    
    // Check if ad should show based on game's ad frequency setting
    const adFrequency = selectedGame?.groupQuizAdFrequency || 5;
    const shouldShowAd = adFrequency > 0 && (newQuestionCount % adFrequency === 0);
    
    // 🔧 PHASE 1: NO INTERSTITIALS - Only banner and static ads
    // All timer modes use static/banner ads only
    // Interstitials removed per client request - will add in Phase 2 if needed
    
    // Proceed directly to timer (no blocking ads)
    proceedAfterAd();
    
    function proceedAfterAd() {
      const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
      const currentQ = questionDatabase[questionIndex];
      
      if (groupQuizTimer === 0) {
        // No timer - show answer immediately (with 5s static ad if frequency hit)
        setShowQuestion(false);
        setShowAnswer(true);
        setSelectedAnswer(correctAnswerIndex);
        
        // FIX BUG #2: Set lastAttemptedQuestion for Challenge button
        setLastAttemptedQuestion({
          questionId: currentQ.question_id || currentQ.questionId || questionIndex,
          questionText: currentQ.question_text,
          correctAnswer: currentQ.correct_answer,
          allAnswers: shuffledAnswers,
          playerAnswer: "No timer - answer revealed",
          wasCorrect: true
        });
        
        // Track in history
        setQuestionHistory(prev => [...prev, {
          questionText: currentQ.question_text,
          correctAnswer: currentQ.correct_answer
        }]);
        return;
      }
      
      // Start regular countdown timer
      setShowStartTimerButton(false);
      setQuizCountdown(groupQuizTimer);
      setTimerActive(true);
      
      console.log('⏱️ TIMER STARTED:', {
        groupQuizTimer,
        'Setting quizCountdown to': groupQuizTimer,
        'showQuestion (before)': showQuestion,
        'showAnswer (before)': showAnswer
      });
      
      const countdown = setInterval(() => {
        setQuizCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            setTimerActive(false);
            
            console.log('⏱️ TIMER COMPLETED - Showing answer now');
            
            // Automatically show answer when timer completes
            setShowQuestion(false);
            setShowAnswer(true);
            setSelectedAnswer(correctAnswerIndex);
            
            // FIX BUG #2: Set lastAttemptedQuestion for Challenge button to work
            const currentQ = questionDatabase[questionIndex];
            setLastAttemptedQuestion({
              questionId: currentQ.question_id || currentQ.questionId || questionIndex,
              questionText: currentQ.question_text,
              correctAnswer: currentQ.correct_answer,
              allAnswers: shuffledAnswers,
              playerAnswer: "Timer completed",
              wasCorrect: true
            });
            
            // Track in history
            setQuestionHistory(prevHistory => [...prevHistory, {
              questionText: currentQ.question_text,
              correctAnswer: currentQ.correct_answer
            }]);
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };
  
  
  const handleOpenChallenge = () => {
    setShowChallengePopup(true);
  };
  
  const handleSubmitChallengeClick = () => {
    if (!challengeIssue) {
      alert('Please select an issue type');
      return;
    }
    
    if (!lastAttemptedQuestion) {
      alert('No question to challenge. Answer a question first.');
      return;
    }
    
    onSubmitChallenge({
      questionId: lastAttemptedQuestion.questionId,
      questionText: lastAttemptedQuestion.questionText,
      correctAnswer: lastAttemptedQuestion.correctAnswer,
      playerAnswer: lastAttemptedQuestion.playerAnswer,
      allAnswers: lastAttemptedQuestion.allAnswers,
      issueType: challengeIssue,
      playerComments: challengeDetails,
      playerId: playerData.playerId,
      playerName: playerData.username
    });
    
    setChallengeIssue('');
    setChallengeDetails('');
    setShowChallengePopup(false);
    alert('Challenge submitted! Admin will review it. You will receive 50 tags if approved.');
  };
  
  // End Game handler - Save scores permanently
  const [gameEnded, setGameEnded] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [isEndingGame, setIsEndingGame] = useState(false);
  
  const handleEndGame = () => {
    // ============================================
    // ✏️ CONFIRMATION: Prevent accidental End Game
    // ============================================
    console.log('🛑 END GAME BUTTON PRESSED!');
    
    // 🔧 FIX 2025.11.27.1820: Add confirmation to prevent accidental taps
    if (!window.confirm('Are you sure you want to end the game and save your scores?')) {
      console.log('❌ End Game cancelled by user');
      return;
    }
    
    console.log('✅ End Game confirmed by user');
    
    // ============================================
    // ✏️ CLICK GUARD: Prevent double-click
    // ============================================
    if (isEndingGame) {
      console.log('⚠️ End Game already processing - ignoring click');
      return;
    }
    setIsEndingGame(true);
    
    // Save current scores to permanent player stats
    // 🔧 FIX 2025.11.27.1700: Include claimedMilestones from state (may have new ones)
    const updatedStats = {
      totalTraks: (playerData.stats.totalTraks || 0) + traksInGame,
      totalTags: (playerData.stats.totalTags || 0) + tagsInGame,
      questionsAnswered: (playerData.stats.questionsAnswered || 0) + attemptedInGame,
      correctAnswers: (playerData.stats.correctAnswers || 0) + correctInGame,
      currentStreak: playerData.stats.currentStreak,
      lastPlayDate: playerData.stats.lastPlayDate,
      claimedMilestones: claimedMilestones // Use state which may have new milestones
    };
    
    // Update playerData with saved scores
    const updatedPlayerData = {
      ...playerData,
      stats: updatedStats
    };
    
    // 🔧 FIX 2025.12.04: DON'T save to localStorage here!
    // App.js handleExitGame will save when player actually exits
    // Saving here AND in handleExitGame causes double-counting
    console.log('💾 Game ended - stats will be saved on Exit:', updatedStats);
    
    // ============================================
    // ✏️ Reset Picture Grid - Clear saved grid state
    // ============================================
    localStorage.removeItem('currentPictureGrid');
    console.log('🔄 Picture Grid reset - all cells available for next game');
    
    // Show end game summary
    setGameEnded(true);
    
    // Reset processing flag after a short delay
    setTimeout(() => {
      setIsEndingGame(false);
    }, 1000);
    
    console.log('✅ Game Ended - Scores Saved:', {
      traksEarned: traksInGame,
      tagsEarned: tagsInGame,
      questionsAnswered: attemptedInGame,
      correctAnswers: correctInGame,
      newTotalTraks: updatedStats.totalTraks,
      newTotalTags: updatedStats.totalTags
    });
  };
  
  const handleStartNewGame = () => {
    // Reset in-game counters
    setCorrectInGame(0);
    setAttemptedInGame(0);
    setTraksInGame(0);
    setTagsInGame(0);
    setSavedTraks(0);
    
    // ============================================
    // ✏️ Reset Picture Grid - Clear saved grid state
    // ============================================
    localStorage.removeItem('currentPictureGrid');
    console.log('🔄 Picture Grid reset - all cells available');
    
    // Reset game state
    setGameEnded(false);
    setShowQuestion(true);
    setShowAnswer(false);
    setSelectedAnswer(null);
    setIsRetry(false);
    setShowVictory(false);
    setPalUsed(false);
    setPalUnlocked(false);
    setPalPlatform(null);
    setIsProcessingAnswer(false); // ✏️ Reset processing flag for new game
    setIsEndingGame(false); // ✏️ Reset end game flag
    setQuizStarted(false); // ✏️ Reset quiz started flag for new game
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Ask a Pal handlers
  const handleAskAPal = () => {
    setShowPalChooser(true);
  };
  
  const handlePlatformChoice = (platform) => {
    console.log(`👥 User selected platform: ${platform}`);
    
    // ============================================
    // ✏️ CRITICAL: Reduce TRAKs by 50% when using Ask a Pal!
    // Use savedTraks because traksInGame is already 0 after wrong answer
    // ============================================
    const currentTraks = savedTraks > 0 ? savedTraks : traksInGame;
    const trakCost = Math.floor(currentTraks * 0.5);
    const newSavedTraks = currentTraks - trakCost;
    
    console.log(`💸 Ask a Pal Cost: ${trakCost} TRAKs (50% of ${currentTraks})`);
    console.log(`📊 TRAKs after cost: ${newSavedTraks}`);
    console.log(`🔍 Using savedTraks: ${savedTraks}, traksInGame: ${traksInGame}`);
    
    // Update savedTraks with the reduced amount
    setSavedTraks(newSavedTraks);
    
    setPalUsed(true);
    setPalPlatform(platform);
    setShowPalChooser(false);
    
    // ============================================
    // ✏️ CRITICAL: Reset processing flag so user can answer
    // ============================================
    setIsProcessingAnswer(false);
    
    // Return to question screen so player can answer after getting help
    setShowQuestion(true);
    setShowAnswer(false);
    setSelectedAnswer(null);
    
    // Use the appropriate question index based on mode
    const questionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
    const currentQ = questionDatabase[questionIndex];
    const messageText = formatMessageForPlatform(platform, currentQ);
    
    if (platform === 'copy') {
      // Copy to clipboard - THIS ACTUALLY WORKS!
      navigator.clipboard.writeText(messageText).then(() => {
        console.log('✅ Message copied to clipboard!');
        alert('Question copied to clipboard! Share it with your pal and come back to answer.');
      }).catch(err => {
        console.error('❌ Copy failed:', err);
        alert('Copy failed. Here\'s the text:\n\n' + messageText);
      });
    } else if (platform === 'whatsapp') {
      // WhatsApp deep link
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(messageText)}`;
      console.log('📱 Opening WhatsApp:', whatsappUrl);
      window.open(whatsappUrl, '_blank');
    } else if (platform === 'facebook') {
      // Facebook share
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(messageText)}`;
      console.log('📘 Opening Facebook:', facebookUrl);
      window.open(facebookUrl, '_blank');
    }

  };
  
  const formatMessageForPlatform = (platform, question) => {
    // 🔧 STANDARDIZED: Use database format fields
    const wrongAnswersList = [
      question.wrong_answer1 || "",
      question.wrong_answer2 || "",
      question.wrong_answer3 || ""
    ].filter(a => a);
    
    const correctAns = question.correct_answer || "";
    const answers = [correctAns, ...wrongAnswersList]
      .sort(() => Math.random() - 0.5); // Randomize so correct answer isn't obvious
    
    if (platform === 'whatsapp') {
      return `🤔 TRAKtag Quiz Question Help Needed!\n"${question.question_text}"\nOptions:\nA) ${answers[0]}\nB) ${answers[1]}\nC) ${answers[2]}\nD) ${answers[3]}\nWhat do you think is the correct answer?\nGet your quiz app at www.TRAKtag.com`;
    } else if (platform === 'facebook') {
      return `🤔 TRAKtag Quiz Question Help Needed!\n"${question.question_text}"\nOptions:\nA) ${answers[0]}\nB) ${answers[1]}\nC) ${answers[2]}\nD) ${answers[3]}\nWhat do you think is the correct answer?\nGet your quiz app at www.TRAKtag.com\n\n#QuizHelp #TRAKtagUK`;
    } else {
      // Copy format
      return `🤔 TRAKtag Quiz Question Help Needed!\n"${question.question_text}"\nOptions:\nA) ${answers[0]}\nB) ${answers[1]}\nC) ${answers[2]}\nD) ${answers[3]}\nWhat do you think is the correct answer?\nGet your quiz app at www.TRAKtag.com`;
    }
  };
  
  // Determine which question index to use based on mode
  const activeQuestionIndex = groupQuizMode ? groupQuizPosition : currentQuestionIndex;
  // 🔧 STANDARDIZED: Fallback uses database format only
  const currentQ = questionDatabase[activeQuestionIndex] || {
    question_text: "No questions available for this game",
    correct_answer: "Please add questions in Game Admin",
    wrong_answer1: "Go to Game Admin",
    wrong_answer2: "Select your game",
    wrong_answer3: "Add questions",
    tag_value: 10,
    active: true,
    game_id: selectedGame?.game_id || 1
  };
// 🔧 FIX: Memoize filtered pictures to prevent new array on every render
  // This prevents PictureGridGame from remounting on every cell click
  const gamePictures = useMemo(() => {
    const filtered = pictures.filter(p => p.game_id === selectedGame?.game_id);
    console.log('🖼️ FILTERED PICTURES:', {
      totalPictures: pictures.length,
      selectedGameId: selectedGame?.game_id,
      filteredCount: filtered.length,
      filtered: filtered.map(p => ({ id: p.pic_id, active: p.active, file_path: p.file_path?.substring(0, 50) }))
    });
    return filtered;
  }, [pictures, selectedGame?.game_id]);

// 🆕 NEW: Show message if no game selected
  if (!selectedGame) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center p-8">
          <h2 className="text-white text-2xl font-bold mb-4">🎮 No Quiz Selected</h2>
          <p className="text-gray-400 mb-4">Please select a quiz from Player Admin to start playing</p>
          <p className="text-gray-500 text-sm">Go to Player Admin → Select Quiz</p>
        </div>
      </div>
    );
  }
  
  // 🔧 FIX: Show message if no questions for selected game
  if (questionDatabase.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center p-8">
          <h2 className="text-white text-2xl font-bold mb-4">📝 No Questions Available</h2>
          <p className="text-gray-400 mb-4">This quiz has no questions yet.</p>
          <p className="text-white text-sm mb-2">Game: {selectedGame?.name}</p>
          <p className="text-gray-500 text-sm">Please ask the admin to add questions to this quiz in Game Admin.</p>
        </div>
      </div>
    );
  }
  
  // 🔧 FIX 2026.01.08.1900: Wait for THIS GAME's pictures to load (not just any pictures)
  // This prevents logo flash AND timer reset issues on SEO URLs like /quiz/baking
  if (showPictureGrid && !groupQuizMode && !gameEnded && gamePictures.length === 0) {
    console.log('⏳ WAITING: Pictures for this game not loaded yet, showing loading screen');
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center p-8">
          <div className="text-4xl mb-4 animate-pulse">🖼️</div>
          <p className="text-white text-lg">Loading {selectedGame?.name || 'quiz'}...</p>
        </div>
      </div>
    );
  }
    
  // Show Picture Grid if active in Player Mode AND game not ended
  if (showPictureGrid && !groupQuizMode && !gameEnded) {
    return (
       <PictureGridScreen
        settings={pictureGridSettings}
        onCellTap={handleGridCellTap}
        tappedCells={gridTappedCells}
        currentPictureIndex={currentPictureIndex}
        onComplete={handleGridComplete}
        playerData={playerData}
        traksInGame={traksInGame}  // 🔧 FIX 2025.11.27.1800: Pass current session TRAKs
        viewMode={selectedGame?.pictureMode || 'picture-on-black'}  // 🆕 NEW
        gamePictures={gamePictures} // 🔧 FIX: Use memoized array
        adminMessage={selectedGame?.grid_message || ''}  // 🔧 FIX 2025.12.12: Use grid_message from database
        selectedGame={selectedGame}  // 🔧 FIX: Pass selectedGame for banner
        gridCount={completedGridCount}  // 🆕 ADD: Track completed grids for picture rotation
      />
    );
  }
  
  return (
    <div className="fixed inset-0 flex flex-col bg-gray-900 max-w-md mx-auto overflow-x-hidden overflow-y-auto z-[8000]">
      {/* 🔧 FIX 2025.11.28: Made full-screen fixed overlay to hide parent elements */}
      
      {/* 🆕 EXIT AD OVERLAY (2025.11.30) */}
      {showExitAd && selectedGame?.exitAdUrl && (
        <div 
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center bg-black/95"
        >
          {/* Sponsor label */}
          <p className="text-gray-400 text-xs mb-2">Message from our Sponsor</p>
          
          {/* Exit Ad Image */}
          <a 
            href={selectedGame.adClickUrl || 'https://traktag.com'} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <img 
              src={selectedGame.exitAdUrl} 
              alt="Sponsor Advertisement"
              className="rounded-lg border-2 border-gray-700"
              style={{maxWidth: '320px', maxHeight: '480px', width: '100%', height: 'auto'}}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </a>
          
          {/* Countdown or Close button */}
          <div className="mt-4">
            {exitAdCountdown > 0 ? (
              <div className="text-gray-400 text-sm">
                Close in <span className="text-yellow-400 font-bold">{exitAdCountdown}</span>...
              </div>
            ) : (
              <button
                onClick={handleCloseExitAd}
                className="bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-8 rounded-lg text-lg"
              >
                ✕ Close & Return Home
              </button>
            )}
          </div>
          
          {/* Thank sponsor text */}
          <p className="text-gray-500 text-xs mt-4">Thank you for supporting TRAKtag Quiz!</p>
        </div>
      )}
      
      {/* 🆕 REGISTRATION PROMPT OVERLAY (2025.12.02) - After 16 questions for anonymous players */}
      {showRegistrationPrompt && (
        <div 
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/95 px-4"
        >
          <div className="bg-gradient-to-br from-purple-800 to-purple-900 rounded-2xl p-6 max-w-sm w-full text-center border-2 border-purple-500 shadow-2xl">
            {/* Trophy icon */}
            <div className="text-6xl mb-4">🏆</div>
            
            <h2 className="text-2xl font-bold text-white mb-3">
              You're Doing Great!
            </h2>
            
            <p className="text-purple-200 mb-4">
              You've answered <span className="text-yellow-400 font-bold">16 questions</span> already!
            </p>
            
            <p className="text-white mb-6">
              Create a free account to <span className="text-green-400 font-bold">save your progress</span>, 
              track your achievements, and compete on leaderboards!
            </p>
            
            {/* Benefits list */}
            <div className="text-left bg-purple-700/50 rounded-lg p-3 mb-6">
              <p className="text-purple-200 text-sm mb-2">✅ Save your TRAKs & stats forever</p>
              <p className="text-purple-200 text-sm mb-2">✅ Unlock all achievements</p>
              <p className="text-purple-200 text-sm mb-2">✅ Compete on leaderboards</p>
              <p className="text-purple-200 text-sm">✅ Play on any device</p>
            </div>
            
            {/* Register button */}
            <button
              onClick={() => {
                setShowRegistrationPrompt(false);
                if (onShowAuth) onShowAuth('register');
              }}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold py-4 px-6 rounded-xl text-lg shadow-lg transform hover:scale-105 transition-all mb-3"
            >
              🚀 Create Free Account
            </button>
            
            <p className="text-gray-400 text-xs">
              Takes less than 30 seconds!
            </p>
          </div>
        </div>
      )}
      
      {/* 🎉 MILESTONE CELEBRATION OVERLAY (2025.11.27.1700) */}
      {showMilestoneCelebration && currentMilestone && !groupQuizMode && (
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          {/* Confetti particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-20px`,
                  width: `${8 + Math.random() * 8}px`,
                  height: `${8 + Math.random() * 8}px`,
                  background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'][Math.floor(Math.random() * 8)],
                  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  animation: `confettiFall ${2 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            ))}
          </div>
          
          {/* Celebration content */}
          <div 
            className="text-center px-8 py-12 rounded-2xl relative"
            style={{
              background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,140,0,0.2) 100%)',
              border: '3px solid #FFD700',
              boxShadow: '0 0 60px rgba(255,215,0,0.5), 0 0 100px rgba(255,215,0,0.3)',
              animation: 'celebrationPop 0.5s ease-out'
            }}
          >
            {/* Sparkles */}
            <div className="absolute -top-4 -left-4 text-4xl" style={{animation: 'sparkle 1s ease-in-out infinite'}}>✨</div>
            <div className="absolute -top-4 -right-4 text-4xl" style={{animation: 'sparkle 1s ease-in-out infinite 0.2s'}}>🎉</div>
            <div className="absolute -bottom-4 -left-4 text-4xl" style={{animation: 'sparkle 1s ease-in-out infinite 0.4s'}}>🎉</div>
            <div className="absolute -bottom-4 -right-4 text-4xl" style={{animation: 'sparkle 1s ease-in-out infinite 0.6s'}}>✨</div>
            
            {/* Header */}
            <div 
              className="text-yellow-300 text-2xl font-black mb-6 tracking-wide"
              style={{textShadow: '0 0 20px rgba(255,215,0,0.8)'}}
            >
              🎊 ACHIEVEMENT UNLOCKED! 🎊
            </div>
            
            {/* Icon */}
            <div 
              className="text-8xl mb-6"
              style={{
                animation: 'iconPulse 0.8s ease-in-out infinite',
                filter: 'drop-shadow(0 0 30px rgba(255,215,0,0.8))'
              }}
            >
              {currentMilestone.icon}
            </div>
            
            {/* Achievement name */}
            <div 
              className="text-white text-3xl font-black mb-2"
              style={{textShadow: '0 0 10px rgba(255,255,255,0.5)'}}
            >
              {currentMilestone.name}
            </div>
            
            {/* 🔧 FIX 2025.11.27.1745: Show TRAKs milestone reached */}
            <div className="text-yellow-400 text-lg font-bold mb-2">
              🏆 {currentMilestone.traks.toLocaleString()} TRAKs Milestone!
            </div>
            
            {/* Tagline */}
            <div className="text-yellow-200 text-lg mb-6 italic">
              "{currentMilestone.tagline}"
            </div>
            
           {/* Bonus TRAKs - Check if this is new player welcome bonus */}
            <div 
              className="bg-gradient-to-r from-green-600 to-green-500 text-white text-xl font-bold py-3 px-6 rounded-full inline-block"
              style={{
                boxShadow: '0 4px 15px rgba(34,197,94,0.5)',
                animation: 'bonusPop 0.5s ease-out 0.3s both'
              }}
            >
              {localStorage.getItem('isNewPlayerBonusCelebration') === 'true' && currentMilestone.traks === 50 ? (
                (() => {
                  localStorage.removeItem('isNewPlayerBonusCelebration');
                  return '🎉 Welcome! You won 50 TRAKs!';
                })()
              ) : (
                `+${currentMilestone.bonusTraks} Bonus TRAKs Awarded!`
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Celebration CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes celebrationPop {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.5; transform: scale(1.2) rotate(10deg); }
        }
        @keyframes bonusPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      
      {/* Contact form moved to FloatingContactButton in App.js */}
      
      {/* Game Ended Summary Screen - Normal Mode */}
      {/* 🔧 FIX 2025.11.28: Redesigned Exit Screen with sponsor ad block */}
      {gameEnded && !groupQuizMode && (
        <div className="flex-1 overflow-y-auto bg-gray-900">
          <div className="w-full max-w-[340px] mx-auto py-4 px-2">
            
            {/* Header with Logo and Game Name on separate line */}
            {/* Game Name Banner */}
            <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-t-lg px-2 py-1 text-center">
              <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md">
                {(() => {
                  const name = selectedGame?.name || selectedGame?.game_name || 'Quiz';
                  return name.length > 35 ? name.substring(0, 35) + '...' : name;
                })()}
              </span>
            </div>
            {/* TRAKs & Streak Banner */}
            <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-blue-600 rounded-b-lg px-2 py-2 mb-3 flex items-center gap-2">
              <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                {compactLogoUrl ? (
                  <img src={compactLogoUrl} alt="TRAKtag" className="w-full h-full object-contain" />
                ) : (
                  <img src="/favicon-48.png" alt="TRAKtag" className="w-full h-full object-contain" />
                )}
              </div>
              <div className="text-white font-bold text-xs">
                🏆 <span className="text-yellow-300">{((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}</span>
              </div>
              <div className="flex-1"></div>
              <div className="text-white font-bold text-xs">
                🔥 <span className="text-orange-300">{playerData?.stats?.currentStreak || 0} days</span>
              </div>
            </div>
            
            {/* Game Over Banner */}
            <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-green-600 rounded-lg p-4 mb-3 text-center border-2 border-yellow-400">
              <h2 className="text-white text-2xl font-black mb-1">🎮 GAME OVER!</h2>
              <p className="text-yellow-300 text-sm font-bold">Scores Saved Successfully! ✅</p>
            </div>
            
            {/* Session Stats - Compact */}
            <div className="bg-gray-800 rounded-lg p-3 mb-3 border border-blue-500/50">
              <h3 className="text-white text-sm font-bold mb-2 text-center">📊 This Session</h3>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-blue-700/50 rounded-lg p-2 text-center">
                  <p className="text-white text-lg font-black">{traksInGame}</p>
                  <p className="text-blue-300 text-xs">TRAKs</p>
                </div>
                <div className="bg-purple-700/50 rounded-lg p-2 text-center">
                  <p className="text-white text-lg font-black">{tagsInGame}</p>
                  <p className="text-purple-300 text-xs">tags</p>
                </div>
                <div className="bg-green-700/50 rounded-lg p-2 text-center">
                  <p className="text-white text-lg font-black">{correctInGame}</p>
                  <p className="text-green-300 text-xs">Correct</p>
                </div>
                <div className="bg-yellow-700/50 rounded-lg p-2 text-center">
                  <p className="text-white text-lg font-black">{attemptedInGame > 0 ? Math.round((correctInGame / attemptedInGame) * 100) : 0}%</p>
                  <p className="text-yellow-300 text-xs">Score</p>
                </div>
              </div>
            </div>
            
            {/* Career Totals - Compact */}
            <div className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 rounded-lg p-3 mb-3 border border-yellow-400/50">
              <h3 className="text-white text-sm font-bold mb-2 text-center">🏆 Career Totals</h3>
              <div className="flex justify-around">
                <div className="text-center">
                  <p className="text-white text-xl font-black">{((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}</p>
                  <p className="text-yellow-200 text-xs">Total TRAKs</p>
                </div>
                <div className="text-center">
                  <p className="text-white text-xl font-black">{((playerData?.stats?.totalTags || 0) + tagsInGame).toLocaleString()}</p>
                  <p className="text-yellow-200 text-xs">Total tags</p>
                </div>
              </div>
            </div>
            
            {/* 🎯 SPONSOR AD BLOCK - Large clickable */}
            <div className="mb-3">
              <p className="text-gray-500 text-xs text-center mb-1">Special Offer from Our Sponsor</p>
              {selectedGame?.exitAdUrl ? (
                <a 
                  href={selectedGame?.exitAdClickUrl || selectedGame?.adClickUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img 
                    src={selectedGame.exitAdUrl}
                    alt="Sponsor"
                    className="w-full rounded-lg border-2 border-yellow-500 shadow-lg"
                    style={{ maxHeight: '250px', objectFit: 'contain', background: '#1a1a2e' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{ display: 'none' }} className="w-full h-48 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-lg flex flex-col items-center justify-center border-2 border-yellow-500">
                    <span className="text-4xl mb-2">🎁</span>
                    <span className="text-white font-bold">Special Offer!</span>
                    <span className="text-yellow-200 text-sm">Tap to learn more</span>
                  </div>
                </a>
              ) : (
                <a 
                  href={selectedGame?.adClickUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-48 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-lg flex flex-col items-center justify-center border-2 border-yellow-500 cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <span className="text-5xl mb-2">🎁</span>
                  <span className="text-white font-bold text-lg">Special Offer!</span>
                  <span className="text-yellow-200 text-sm">From {selectedGame?.game_name || 'Our Sponsor'}</span>
                  <span className="text-white text-xs mt-2 bg-white/20 px-3 py-1 rounded-full">Tap to view →</span>
                </a>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleStartNewGame}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-black py-4 px-4 rounded-lg text-lg shadow-lg"
              >
                🎮 Play Again
              </button>
              <button
                onClick={() => setGameEnded(false)}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg text-sm"
              >
                ← Continue Current Game
              </button>
              <button
                onClick={() => {
                  setGameEnded(false);
                  handleExitWithAd();
                }}
                className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-4 rounded-lg text-sm border border-red-400"
              >
                🏠 Return Home
              </button>
            </div>
            
            {/* Share Score */}
            <div className="mt-3 bg-blue-900/50 rounded-lg p-3 text-center">
              <p className="text-blue-200 text-xs mb-2">Share your score with friends!</p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => {
                    const text = `🎯 I just scored ${traksInGame} TRAKs on TRAKtag Quiz!\n\n🔥 ${playerData?.stats?.currentStreak || 0} day streak!\n\nCan you beat me?`;
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  className="bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-1"
                >
                  💬 WhatsApp
                </button>
                <button
                  onClick={() => {
                    const text = `I just scored ${traksInGame} TRAKs on TRAKtag Quiz! 🎯 Can you beat me?`;
                    navigator.clipboard.writeText(text);
                    alert('Score copied! Share it with your friends!');
                  }}
                  className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-1"
                >
                  📋 Copy
                </button>
              </div>
            </div>
            
          </div>
        </div>
      )}
      
      {/* Quiz Summary Screen - Group Quiz Mode Only */}
      {quizEnded && groupQuizMode && (
        <div className="fixed inset-0 z-[9000] bg-gray-900 overflow-y-auto">
          <div className="max-w-md mx-auto p-3 pb-20">
            {/* Header - compact, sticky */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-4 mb-3 text-center">
              <h2 className="text-white text-2xl font-bold mb-1">🏁 Quiz Complete!</h2>
              <p className="text-white text-sm">Questions Answered: {zeroTimerMode ? sessionQuestions.length : questionHistory.length}</p>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => {
                  setQuizEnded(false);
                  setQuestionHistory([]);
                  setSessionQuestions([]);
                  setCorrectInGame(0);
                  setAttemptedInGame(0);
                  setQuestionsInSession(0);
                  if (zeroTimerMode) {
                    setCurrentScreen(1);
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-3 rounded-lg text-sm"
              >
                🔄 Start New Quiz
              </button>
              <button
                onClick={() => setQuizEnded(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-3 rounded-lg text-sm"
              >
                ← Back to Quiz
              </button>
            </div>
            
            {/* END GAME button - prominent */}
            <button
              onClick={() => {
                setQuizEnded(false);
                handleExitWithAd();
              }}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-4 rounded-lg mb-3 border-2 border-red-400"
            >
              🏠 END GAME & Return Home
            </button>
            
            {/* Questions list - scrolls naturally with page */}
            <div className="space-y-2">
              {(zeroTimerMode ? sessionQuestions : questionHistory).map((item, index) => (
                <div key={index} className="bg-gray-800 rounded-lg p-3 border border-gray-600">
                  <p className="text-white font-bold text-xs mb-1">Q{index + 1}: {item.questionText}</p>
                  <div className="bg-green-900/50 border border-green-500 rounded p-2">
                    <p className="text-green-400 text-xs">✓ {item.correctAnswer}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Bottom padding for scroll */}
            <div className="h-4"></div>
          </div>
        </div>
      )}
      
      {/* Regular Quiz Display - Hidden when quiz ended or game ended */}
      {!quizEnded && !gameEnded && (
        <>
      
      {/* Timeout Message Overlay */}
      {showTimeoutMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="bg-red-600 text-white px-12 py-10 rounded-xl shadow-2xl max-w-md mx-4 text-center border-8 border-yellow-400 animate-pulse">
            <div className="text-8xl mb-6">⏰</div>
            <div className="text-4xl font-black mb-6">TIME'S UP!</div>
            <div className="text-2xl font-bold mb-4">You ran out of time!</div>
            <div className="bg-yellow-400 text-black px-6 py-4 rounded-lg text-xl font-black mb-4">
              💔 LOST YOUR TRAKs!
            </div>
            <div className="text-lg font-bold mb-2">But you can recover them...</div>
            <div className="text-base">Watch an ad to retry or skip to see the answer!</div>
          </div>
        </div>
      )}
      
      {/* 🆕 2025.12.23: TIME OUT SCREEN - Shows after timeout, before Brain Glitch */}
      {showTimeOutScreen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-violet-700 px-4 py-3 text-center">
            <div className="font-bold text-white">{selectedGame?.name || selectedGame?.game_name || '🎯 QUIZ'}</div>
            <div className="flex justify-center gap-6 text-sm text-white/90 mt-1">
              <span>🏆 {(playerData?.stats?.totalTraks || 0) + traksInGame} TRAKs</span>
              <span>🔥 {playerData?.stats?.currentStreak || 0} days</span>
            </div>
          </div>
          
          {/* Red Hero Section */}
          <div className="bg-gradient-to-b from-red-600 to-red-800 flex-1 flex flex-col items-center justify-center px-4 py-6">
            <div className="text-6xl mb-3">⏰</div>
            <div className="text-3xl font-black text-white mb-4">Time's Up!</div>
            
            {/* Recovery Message Box */}
            <div className="bg-black/30 rounded-xl p-4 max-w-sm w-full text-center mb-6">
              <div className="text-xl font-bold text-yellow-400 mb-2">LOST YOUR TRAKs??? 😱</div>
              <div className="text-white text-base mb-2">But you can recover them...</div>
              <div className="text-white/80 text-sm">Watch an Ad to retry or skip to see the answer</div>
            </div>
            
            {/* Speed Adjustment */}
            <div className="bg-black/30 rounded-xl p-4 max-w-sm w-full mb-6">
              <div className="text-yellow-400 text-xs font-medium text-center mb-3">⚡ Adjust your speed:</div>
              <div className="flex gap-2 justify-center">
                {speedOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => handleSpeedModeChange(option.id)}
                    className={`flex-1 max-w-[70px] py-2 px-1 rounded-lg text-center transition-all ${
                      selectedSpeedMode === option.id
                        ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-lg">{option.icon}</div>
                    <div className="text-[10px] font-medium">{option.name}</div>
                    <div className="text-[9px] opacity-70">{option.multiplier}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Continue Button */}
            <button
              onClick={handleTimeOutContinue}
              className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-4 px-8 rounded-xl text-lg max-w-sm w-full shadow-lg transition-all"
            >
              Continue →
            </button>
          </div>
          
          {/* Ad Banner */}
          <div className="bg-gray-800 p-3 text-center">
            <div className="bg-gray-700 rounded-lg py-4 px-6 text-gray-400 text-sm border border-gray-600 border-dashed">
              📺 Sponsor Ad (320×50)
            </div>
          </div>
        </div>
      )}
      
      
      <div className="flex-1 flex flex-col p-2 pb-16">
        {/* ✏️ pb-16 adds space for fixed banner ad at bottom */}
        {/* ZERO-TIMER MODE SCREENS - Mobile First Design */}
        {zeroTimerMode && !quizEnded && (
          <>
            {/* Screen 1: Initial/Waiting */}
            {currentScreen === 1 && (
              <div className="flex flex-col h-full">
                {/* TOP BANNER: Favicon + Group Quiz + Quiz Name - DEEPER */}
                <div className="bg-green-600 rounded-lg px-4 py-4 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/favicon-48.png" alt="" className="w-6 h-6" onError={(e) => e.target.style.display='none'} />
                    <p className="text-white font-bold text-base">GROUP QUIZ</p>
                  </div>
                  <p className="text-white font-bold text-base">{selectedGame?.name || selectedGame?.game_name || 'QUIZ'}</p>
                </div>
                
                {/* Click to Start - DEEPER */}
                <div 
                  className="bg-green-500 hover:bg-green-400 rounded-lg py-4 px-4 text-center cursor-pointer mb-3"
                  onClick={handleScreenClick}
                >
                  <span className="text-white text-lg font-bold">👉 CLICK TO START QUIZ</span>
                </div>
                
                {/* Static Ad (300x250) - NOT interstitial for Zero Timer */}
                <div className="flex-1 flex items-center justify-center">
                  {selectedGame?.staticAdUrl ? (
                    <a 
                      href={selectedGame?.adClickUrl || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={selectedGame.staticAdUrl} 
                        alt="Sponsor" 
                        className="max-w-[300px] max-h-[250px] rounded-lg border-2 border-yellow-400"
                      />
                    </a>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-6 text-center border-2 border-yellow-400 w-[300px] h-[250px] flex flex-col items-center justify-center">
                      <div className="text-yellow-300 text-5xl mb-2">📺</div>
                      <div className="text-white text-xl font-bold mb-1">ADVERTISEMENT</div>
                      <div className="text-yellow-200 text-sm">300x250 Static</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Screen 2: Question Display */}
            {currentScreen === 2 && showQuestion && !showAnswer && (
              <div className="flex flex-col h-full">
                {/* TOP BANNER: Favicon + Group Quiz + Quiz Name - DEEPER */}
                <div className="bg-green-600 rounded-lg px-4 py-4 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/favicon-48.png" alt="" className="w-6 h-6" onError={(e) => e.target.style.display='none'} />
                    <p className="text-white font-bold text-base">GROUP QUIZ</p>
                  </div>
                  <p className="text-white font-bold text-base">{selectedGame?.name || selectedGame?.game_name || 'QUIZ'}</p>
                </div>
                
                {/* Question Box - DEEPER */}
                <div className="bg-blue-800 rounded-lg p-4 mb-3">
                  <p className="text-white text-lg font-bold text-center">{currentQ.question_text}</p>
                </div>
                
                {/* Click for Answer - DEEPER */}
                <div 
                  className="bg-orange-500 hover:bg-orange-400 rounded-lg py-4 px-4 text-center cursor-pointer mb-3"
                  onClick={handleScreenClick}
                >
                  <span className="text-white text-lg font-bold">👉 CLICK FOR ANSWER</span>
                </div>
                
                {/* Static Ad (300x250) - NOT interstitial for Zero Timer */}
                <div className="flex-1 flex items-center justify-center">
                  {selectedGame?.staticAdUrl ? (
                    <a 
                      href={selectedGame?.adClickUrl || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={selectedGame.staticAdUrl} 
                        alt="Sponsor" 
                        className="max-w-[300px] max-h-[250px] rounded-lg border-2 border-yellow-400"
                      />
                    </a>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-6 text-center border-2 border-yellow-400 w-[300px] h-[250px] flex flex-col items-center justify-center">
                      <div className="text-yellow-300 text-5xl mb-2">📺</div>
                      <div className="text-white text-xl font-bold mb-1">ADVERTISEMENT</div>
                      <div className="text-yellow-200 text-sm">300x250 Static</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Screen 3: Answer Display - REDESIGNED per spec */}
            {currentScreen === 3 && showAnswer && (
              <div className="flex flex-col min-h-full">
                {/* TOP BANNER: Favicon + Group Quiz + Quiz Name - DEEPER */}
                <div className="bg-green-600 rounded-lg px-4 py-4 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/favicon-48.png" alt="" className="w-8 h-8" onError={(e) => e.target.style.display='none'} />
                    <p className="text-white font-bold text-lg">GROUP QUIZ</p>
                  </div>
                  <p className="text-white font-bold text-lg">{selectedGame?.name || selectedGame?.game_name || 'QUIZ'}</p>
                </div>
                
                {/* Correct Answer Box - DEEPER */}
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg py-5 px-4 text-center mb-3">
                  <p className="text-green-800 text-sm font-bold mb-1">✅ CORRECT ANSWER:</p>
                  <p className="text-gray-900 text-3xl font-black">{currentQ.correct_answer}</p>
                </div>
                
                {/* Click for Next - DEEPER */}
                <div 
                  className="bg-green-600 hover:bg-green-500 rounded-lg py-4 px-4 text-center cursor-pointer mb-3"
                  onClick={handleScreenClick}
                >
                  <span className="text-white text-lg font-bold">👆 CLICK FOR NEXT QUESTION</span>
                </div>
                
                {/* Static Ad (300x250) - centered */}
                <div className="flex items-center justify-center mb-3">
                  {selectedGame?.staticAdUrl ? (
                    <a 
                      href={selectedGame?.adClickUrl || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={selectedGame.staticAdUrl} 
                        alt="Sponsor" 
                        className="max-w-[300px] max-h-[250px] rounded-lg border-2 border-yellow-400"
                      />
                    </a>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-6 text-center border-2 border-yellow-400 w-[300px] h-[250px] flex flex-col items-center justify-center">
                      <div className="text-yellow-300 text-5xl mb-2">📺</div>
                      <div className="text-white text-xl font-bold mb-1">ADVERTISEMENT</div>
                      <div className="text-yellow-200 text-sm">300x250 Static</div>
                    </div>
                  )}
                </div>
                
                {/* Spacer to push buttons to bottom */}
                <div className="flex-1"></div>
                
                {/* Challenge & Review buttons at VERY BOTTOM */}
                <div className="flex justify-center gap-3 pb-4 mt-auto">
                 {localStorage.getItem('trak_token') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLastAttemptedQuestion({
                        questionId: groupQuizPosition,
                        questionText: currentQ.question_text,
                        correctAnswer: currentQ.correct_answer,
                        allAnswers: shuffledAnswers,
                        playerAnswer: "Group Quiz Mode",
                        wasCorrect: true
                      });
                      setShowChallengePopup(true);
                    }}
                    className="bg-orange-500 hover:bg-orange-400 text-white py-3 px-6 rounded-lg text-sm font-bold flex items-center gap-2" 
                  >
                    🚩 Challenge
                  </button>
                  )}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReviewQuestions();
                    }}
                    className="bg-blue-700 hover:bg-blue-600 text-white py-3 px-6 rounded-lg text-sm font-bold flex items-center gap-2"
                  >
                    📋 Review
                  </button>
                </div>
              </div>
            )}
            
            {/* Screen 4: Next Question (same layout as Screen 2) */}
            {currentScreen === 4 && showQuestion && !showAnswer && (
              <div className="flex flex-col h-full">
                {/* TOP BANNER: Favicon + Group Quiz + Quiz Name - DEEPER */}
                <div className="bg-green-600 rounded-lg px-4 py-4 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/favicon-48.png" alt="" className="w-6 h-6" onError={(e) => e.target.style.display='none'} />
                    <p className="text-white font-bold text-base">GROUP QUIZ</p>
                  </div>
                  <p className="text-white font-bold text-base">{selectedGame?.name || selectedGame?.game_name || 'QUIZ'}</p>
                </div>
                
                {/* Question Box - DEEPER */}
                <div className="bg-blue-800 rounded-lg p-4 mb-3">
                  <p className="text-white text-lg font-bold text-center">{currentQ.question_text}</p>
                </div>
                
                {/* Click for Answer - DEEPER */}
                <div 
                  className="bg-orange-500 hover:bg-orange-400 rounded-lg py-4 px-4 text-center cursor-pointer mb-3"
                  onClick={handleScreenClick}
                >
                  <span className="text-white text-lg font-bold">👉 CLICK FOR ANSWER</span>
                </div>
                
                {/* Static Ad (300x250) - NOT interstitial for Zero Timer */}
                <div className="flex-1 flex items-center justify-center">
                  {selectedGame?.staticAdUrl ? (
                    <a 
                      href={selectedGame?.adClickUrl || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={selectedGame.staticAdUrl} 
                        alt="Sponsor" 
                        className="max-w-[300px] max-h-[250px] rounded-lg border-2 border-yellow-400"
                      />
                    </a>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-6 text-center border-2 border-yellow-400 w-[300px] h-[250px] flex flex-col items-center justify-center">
                      <div className="text-yellow-300 text-5xl mb-2">📺</div>
                      <div className="text-white text-xl font-bold mb-1">ADVERTISEMENT</div>
                      <div className="text-yellow-200 text-sm">300x250 Static</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        
        {/* EXISTING TIMED MODE AND NORMAL MODE CONTENT */}
        {console.log('🔍 RENDER CHECK:', {
          zeroTimerMode,
          showQuestion,
          showAnswer,
          groupQuizMode,
          willRender: !zeroTimerMode && showQuestion && !showAnswer
        })}
        {!zeroTimerMode && showQuestion && !showAnswer && (
          <>
            {console.log('🎮 RENDERING QUESTION - groupQuizMode:', groupQuizMode, 'currentQ:', currentQ?.question_text?.substring(0, 50))}
            
            {/* 🔧 FIX 2025.12.03: Player Mode Header - game name on separate line */}
            {!groupQuizMode && (
              <div className="w-full max-w-[340px] mx-auto mb-1">
                {/* Game Name Banner - Own line, max 35 chars */}
                <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-t-lg px-2 py-1 text-center">
                  <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md">
                    {(() => {
                      const name = selectedGame?.name || selectedGame?.game_name || 'Quiz';
                      return name.length > 35 ? name.substring(0, 35) + '...' : name;
                    })()}
                  </span>
                </div>
                {/* TRAKs & Streak Banner with Logo */}
                <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-blue-600 rounded-b-lg px-2 py-2 flex items-center gap-2">
                  {/* Logo placeholder - 36x36 */}
                  <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                    {compactLogoUrl ? (
                      <img src={compactLogoUrl} alt="TRAKtag" className="w-full h-full object-contain" />
                    ) : (
                      <img src="/favicon-48.png" alt="TRAKtag" className="w-full h-full object-contain" />
                    )}
                  </div>
                  <div className="text-white font-bold text-xs">
                    🏆 <span className="text-yellow-300">{((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}</span>
                  </div>
                  <div className="flex-1"></div>
                  <div className="text-white font-bold text-xs">
                    🔥 <span className="text-orange-300">{playerData?.stats?.currentStreak || 0} days</span>
                  </div>
                </div>
                
                {/* Session Scores */}
                <div className="bg-gray-800 rounded-lg px-3 py-1 mt-1 flex flex-col text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">
                      ✅ {correctInGame}/{attemptedInGame}
                    </span>
                    <span className="text-green-400 font-bold">
                      +{traksInGame} {traksInGame === 1 ? 'TRAK' : 'TRAKs'}
                    </span>
                    <span className="text-purple-400 font-bold">
                      +{tagsInGame} {tagsInGame === 1 ? 'tag' : 'tags'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-700">
                    <span className="text-gray-400">Totals:</span>
                    <span className="text-green-300 text-[10px]">
                      🏆 {((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}
                    </span>
                    <span className="text-purple-300 text-[10px]">
                      🎯 {((playerData?.stats?.totalTags || 0) + tagsInGame).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* 🔧 FIX 2025.12.03: Group Quiz Mode Header - Game name on own line */}
            {groupQuizMode && !showInterstitialAd && (
              <div className="w-full max-w-[340px] mx-auto mb-1">
                {/* Game Name Banner - Own line, max 35 chars */}
                <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-t-lg px-2 py-1 text-center">
                  <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md">
                    {(() => {
                      const name = selectedGame?.name || selectedGame?.game_name || 'Quiz';
                      return name.length > 35 ? name.substring(0, 35) + '...' : name;
                    })()}
                  </span>
                </div>
                {/* Group Quiz Banner */}
                <div className={`bg-green-600 rounded-b-lg px-3 py-2 flex items-center justify-between ${timerActive && quizCountdown > 0 ? 'animate-pulse' : ''}`}>
                  <div className="flex items-center gap-2">
                    <img src="/favicon-48.png" alt="" className="w-7 h-7" onError={(e) => e.target.style.display='none'} />
                    <p className="text-white font-bold text-sm">GROUP QUIZ</p>
                  </div>
                  {/* Timer display - shows countdown when active, or initial value when waiting */}
                  {groupQuizTimer > 0 && (
                    <div className="text-white font-bold text-lg flex items-center gap-1">
                      🕐 {timerActive ? quizCountdown : groupQuizTimer}s
                    </div>
                  )}
                  {groupQuizTimer === 0 && (
                    <div className="text-white font-bold text-xs">
                      👥 Zero Timer
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* 🔧 FIX: Blocking Interstitial Ad BEFORE timer starts (25 & 35 sec modes only) */}
            {groupQuizMode && showInterstitialAd && !timerActive && groupQuizTimer >= 25 && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95">
                <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-12 mx-4 text-center border-4 border-yellow-400 max-w-md">
                  <div className="text-yellow-300 text-8xl mb-6">📺</div>
                  <div className="text-white text-4xl font-bold mb-4">ADVERTISEMENT</div>
                  <div className="text-yellow-200 text-xl mb-2">Interstitial Ad Space</div>
                  <div className="text-white text-sm opacity-75 mb-6">320x480 Full Screen Ad</div>
                  
                  {/* Countdown Timer */}
                  {interstitialAdCountdown > 0 && (
                    <div className="bg-white bg-opacity-20 rounded-lg p-6 mt-6">
                      <p className="text-yellow-300 text-6xl font-black mb-3">
                        {interstitialAdCountdown}
                      </p>
                      <p className="text-white text-lg">Starting quiz in...</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Mode Indicator - Only show in normal mode - Compact version */}
            {!groupQuizMode && (
            <div className="w-full max-w-[340px] mx-auto bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg px-3 py-2 mb-2 flex justify-between items-center">
              <span className="text-white font-bold text-sm">{modeConfig[safeCurrentMode].name}</span>
              {modeConfig[safeCurrentMode].timer && (
                <span className="text-xl font-bold text-green-400">
                  ⏱️ {timeRemaining !== null ? timeRemaining : modeConfig[safeCurrentMode].timer}s
                </span>
              )}
              {!modeConfig[safeCurrentMode].timer && (
                <span className="text-blue-200 text-sm">Player Mode</span>
              )}
            </div>
            )}
            
            {/* Question Box - More space for longer questions */}
            <div className="w-full max-w-[340px] mx-auto bg-blue-800 rounded-lg px-3 py-3 mb-1">
              <p className={`text-white font-bold text-center ${getQuestionTextSize(currentQ.question_text)}`}>
                {currentQ.question_text}
              </p>
              {!groupQuizMode && (
                <p className="text-blue-300 text-xs text-center mt-1">
                  Worth {Math.floor((currentQ.difficulty_points || currentQ.tag_value || 10) * modeConfig[safeCurrentMode].tagsMultiplier)} {Math.floor((currentQ.difficulty_points || currentQ.tag_value || 10) * modeConfig[safeCurrentMode].tagsMultiplier) === 1 ? 'tag' : 'tags'}
                </p>
              )}
            </div>
            
            
            {palUsed && (
              <div className="mb-4 bg-purple-900 border-2 border-purple-500 rounded-lg p-3">
                <p className="text-purple-200 text-sm text-center">
                  👥 Asked a pal via {palPlatform === 'copy' ? 'clipboard' : palPlatform}! But answer to continue anytime!! Good luck! 🍀
                </p>
              </div>
            )}
            
            {/* 🆕 2026.01.08.2000: Ask a Pal Button - Only in Relaxed/Scenic Mode */}
            {!groupQuizMode && safeCurrentMode === 'relaxed' && !palUsed && traksInGame > 0 && (
              <div className="w-full max-w-[340px] mx-auto mb-2">
                <button
                  onClick={handleAskAPal}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  <span>👥</span>
                  <span>Ask a Pal</span>
                  <span className="text-purple-200 text-sm">(-50% TRAKs)</span>
                </button>
              </div>
            )}
            
            {/* Group Quiz Mode: Start Timer Button - Compact */}
            {groupQuizMode && showStartTimerButton && !showInterstitialAd && (
              <div className="w-full max-w-[340px] mx-auto mb-1">
                <button
                  onClick={handleStartTimer}
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-3 rounded-lg text-sm"
                >
                  {groupQuizTimer === 0 ? '👆 PRESS TO VIEW ANSWER' : `👆 PRESS TO START ${groupQuizTimer} SECOND TIMER`}
                </button>
              </div>
            )}
            
            {/* Countdown is now shown in header banner above */}
            
            {/* Answers Section - Tightened layout for Group Quiz */}
            {console.log('🎯 ANSWER DISPLAY CHECK:', {
              groupQuizMode,
              showStartTimerButton,
              timerActive,
              showInterstitialAd,
              willHideAnswers: groupQuizMode && (showStartTimerButton || timerActive || showInterstitialAd)
            })}
            {groupQuizMode && (showStartTimerButton || timerActive || showInterstitialAd) ? (
              <div className="w-full max-w-[340px] mx-auto">
                {/* Answers Hidden Message - Compact */}
                <div className="bg-gray-800 rounded-lg py-2 px-3 mb-1 text-center">
                  <p className="text-gray-400 text-xs">
                    {showInterstitialAd ? 'Watching advertisement...' : 'ANSWERS HIDDEN UNTIL TIMER FINISHES'}
                  </p>
                </div>
                
                {/* Static Ad (300x250) - for 10s/15s timers */}
                {!showInterstitialAd && (
                  <div className="flex items-center justify-center mb-1">
                    {selectedGame?.staticAdUrl ? (
                      <a 
                        href={selectedGame?.adClickUrl || '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img 
                          src={selectedGame.staticAdUrl} 
                          alt="Sponsor" 
                          className="max-w-[300px] max-h-[250px] rounded-lg border-2 border-yellow-400"
                        />
                      </a>
                    ) : (
                      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-3 text-center border-2 border-yellow-400 w-[300px] h-[220px] flex flex-col items-center justify-center">
                        <div className="text-yellow-300 text-3xl mb-1">📺</div>
                        <div className="text-white text-base font-bold">ADVERT 300x250</div>
                        <div className="text-yellow-200 text-xs">Static</div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* End Quiz / Review Quiz buttons at bottom - Compact */}
                <div className="flex gap-2">
                  <button 
                    onClick={() => setQuizEnded(true)} 
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 px-2 rounded-lg text-xs font-bold"
                  >
                    🛑 END QUIZ
                  </button>
                  <button 
                    onClick={handleReviewQuestions} 
                    className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 px-2 rounded-lg text-xs font-bold"
                  >
                    📋 REVIEW QUIZ
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-[340px] mx-auto space-y-2">
                {shuffledAnswers.map((answer, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerClick(index)}
                    className="w-full py-3 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm"
                  >
                    {answer}
                  </button>
                ))}
              </div>
            )}
            
            {/* 📺 Static Ad Display - only for non-group quiz */}
            {!groupQuizMode && <StaticAd />}
            
            {/* Banner ad - clean, no border, at bottom */}
            {groupQuizMode && (
              <div className="mt-auto">
                {/* Ad removed - now using fixed ad at bottom */}
              </div>
            )}
          </>
        )}
        
        {/* 🛡️ FIX: Added !zeroTimerMode to prevent double screen in Group Quiz */}
        {!zeroTimerMode && showAnswer && (
          <div className="flex flex-col h-full overflow-y-auto">
            
            {/* 🔧 FIX 2025.12.03: Answer screen header - game name on separate line */}
            {!groupQuizMode && !watchingAd && (
              <div className="w-full max-w-[340px] mx-auto mb-2">
                {/* Game Name Banner - Own line, max 35 chars */}
                <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-t-lg px-2 py-1 text-center">
                  <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md">
                    {(() => {
                      const name = selectedGame?.name || selectedGame?.game_name || 'Quiz';
                      return name.length > 35 ? name.substring(0, 35) + '...' : name;
                    })()}
                  </span>
                </div>
                {/* TRAKs & Streak Banner with Logo */}
                <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-blue-600 rounded-b-lg px-2 py-2 flex items-center gap-2">
                  {/* Logo placeholder - 36x36 */}
                  <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                    {compactLogoUrl ? (
                      <img src={compactLogoUrl} alt="TRAKtag" className="w-full h-full object-contain" />
                    ) : (
                      <img src="/favicon-48.png" alt="TRAKtag" className="w-full h-full object-contain" />
                    )}
                  </div>
                  <div className="text-white font-bold text-xs">
                    🏆 <span className="text-yellow-300">{((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}</span>
                  </div>
                  <div className="flex-1"></div>
                  <div className="text-white font-bold text-xs">
                    🔥 <span className="text-orange-300">{playerData?.stats?.currentStreak || 0} days</span>
                  </div>
                </div>
                
                {/* Session Scores */}
                <div className="bg-gray-800 rounded-lg px-3 py-1 mt-1 flex flex-col text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">
                      ✅ {correctInGame}/{attemptedInGame}
                    </span>
                    <span className="text-green-400 font-bold">
                      +{traksInGame} {traksInGame === 1 ? 'TRAK' : 'TRAKs'}
                    </span>
                    <span className="text-purple-400 font-bold">
                      +{tagsInGame} {tagsInGame === 1 ? 'tag' : 'tags'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-700">
                    <span className="text-gray-400">Totals:</span>
                    <span className="text-green-300 text-[10px]">
                      🏆 {((playerData?.stats?.totalTraks || 0) + traksInGame).toLocaleString()}
                    </span>
                    <span className="text-purple-300 text-[10px]">
                      🎯 {((playerData?.stats?.totalTags || 0) + tagsInGame).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {watchingAd ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-purple-700 rounded-lg p-8 w-full max-w-sm">
                  <p className="text-white text-2xl font-bold text-center mb-4">📺 Watching Ad...</p>
                  <div className="bg-purple-900 rounded-full h-20 w-20 mx-auto flex items-center justify-center">
                    <span className="text-white text-4xl font-bold">{adCountdown}</span>
                  </div>
                </div>
              </div>
            ) : showVictory ? (
              <div 
                className="flex-1 flex items-center justify-center"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg p-8 w-full max-w-sm">
                  <p className="text-white text-3xl font-bold text-center mb-4">🎉 WELL DONE!! 🎉</p>
                  <p className="text-white text-lg text-center mb-2">{victoryMessage}</p>
                  <div className="mt-6 border-t border-yellow-300 pt-4">
                    <p className="text-white text-lg text-center font-semibold animate-pulse">
                      ⬅️ Swipe LEFT to continue
                    </p>
                    <p className="text-yellow-200 text-xs text-center mt-1">
                      or tap buttons below
                    </p>
                  </div>
                  <div className={`mt-4 flex flex-col gap-3 ${playerData.handedness === 'left' ? 'items-start' : 'items-end'}`}>
                    <button
                      onClick={handleNextQuestion}
                      className="bg-green-600 hover:bg-green-500 text-white font-bold py-4 px-8 rounded-lg text-lg w-64"
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-1">⬅️</div>
                        <div>Swipe or Tap to Continue</div>
                      </div>
                    </button>
                    {/* 🔧 FIX 2025.11.28: Delay showing these buttons to prevent accidental taps */}
                    {showActionButtons && (
                      <>
                        {localStorage.getItem('trak_token') && (
                        <button
                          onClick={handleOpenChallenge} 
                          onMouseEnter={() => setHoveringButton(true)}
                          onMouseLeave={() => setHoveringButton(false)}
                          className="bg-yellow-600 hover:bg-yellow-500 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2 w-64 justify-center"
                        >
                          <span>🚩</span>
                          <span>Question Challenge</span>
                        </button>
                        )}
                        <button
                          onClick={handleEndGame}
                          onMouseEnter={() => setHoveringButton(true)}
                          onMouseLeave={() => setHoveringButton(false)}
                          className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 w-64 justify-center"
                        >
                          <span>💾</span>
                          <span>End Game</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : groupQuizMode ? (
              // GROUP QUIZ MODE - Show only correct answer
              <>
                {/* Timer countdown display for group quiz */}
                {timerActive && quizCountdown > 0 && (
                  <div className="bg-gray-800 rounded-lg p-6 mb-4 text-center">
                    <p className="text-yellow-400 text-4xl font-black">
                      ⏱️ {quizCountdown}
                    </p>
                    <p className="text-white text-xl font-bold mt-2">{quizCountdown} seconds until answer</p>
                    <p className="text-gray-400 text-sm mt-1">Get ready...</p>
                  </div>
                )}
                
                
                
                {/* 325x250 Banner during countdown */}
                {timerActive && quizCountdown > 0 && quizCountdown % 10 === 0 && (
                  <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-8 mb-4 text-center border-2 border-yellow-400 min-h-[250px] flex flex-col items-center justify-center">
                    <div className="text-yellow-300 text-6xl mb-3">📺</div>
                    <div className="text-white text-2xl font-bold mb-2">ADVERTISEMENT</div>
                    <div className="text-yellow-200 text-sm mb-1">Timer Ad Space</div>
                    <div className="text-white text-xs opacity-75">325x250 Banner Ad</div>
                  </div>
                )}
                {/* Interstitial Ad for timed modes (25 & 35 sec) */}
                {showInterstitialAd && groupQuizTimer !== 0 && (
                  <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-lg p-12 mb-3 text-center border-2 border-yellow-400 min-h-[350px] flex flex-col items-center justify-center">
                    <div className="text-yellow-300 text-7xl mb-4">📺</div>
                    <div className="text-white text-3xl font-bold mb-3">ADVERTISEMENT</div>
                    <div className="text-yellow-200 text-base mb-2">Interstitial Ad Space</div>
                    <div className="text-white text-sm opacity-75 mb-4">320x480 Interstitial Ad</div>
                    
                    {/* 🔧 FIX: Show countdown timer */}
                    {interstitialAdCountdown > 0 && (
                      <div className="mt-6 bg-white bg-opacity-20 rounded-lg p-4">
                        <p className="text-yellow-300 text-5xl font-black mb-2">
                          {interstitialAdCountdown}
                        </p>
                        <p className="text-white text-sm">Closing automatically...</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* 🔧 FIX: Show answer for Group Quiz after timer completes */}
                {!timerActive && !showInterstitialAd && (
                  <>
                    {/* TOP BANNER: Favicon + Group Quiz + Quiz Name */}
                    <div className="bg-green-600 rounded-lg px-4 py-4 mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src="/favicon-48.png" alt="" className="w-10 h-10" onError={(e) => e.target.style.display='none'} />
                        <p className="text-white font-bold text-lg">GROUP QUIZ</p>
                      </div>
                      <p className="text-white font-bold text-lg">{selectedGame?.name || selectedGame?.game_name || 'QUIZ'}</p>
                    </div>
                    
                    {/* Correct Answer - compact */}
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg py-4 px-4 text-center mb-3">
                      <p className="text-yellow-900 text-sm font-bold mb-1">✅ CORRECT ANSWER:</p>
                      <p className="text-yellow-900 text-3xl font-black">{currentQ.correct_answer}</p>
                    </div>
                    
                    {/* Next Question Button - compact */}
                    <button
                      onClick={handleNextQuestion}
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-4 px-4 rounded-lg text-lg mb-3"
                    >
                      👆 CLICK FOR NEXT QUESTION
                    </button>
                    
                    {/* 📺 Static Ad Display - only show if no interstitial */}
                    <StaticAd />
                    
                    {/* Challenge & Review buttons at BOTTOM */}
                    <div className="flex justify-center gap-3 mt-4 pb-2">
                      {localStorage.getItem('trak_token') && (
                      <button
                        onClick={() => {
                          setLastAttemptedQuestion({
                            questionId: groupQuizPosition,
                            questionText: currentQ.question_text,
                            correctAnswer: currentQ.correct_answer,
                            allAnswers: shuffledAnswers,
                            playerAnswer: "Group Quiz Mode",
                            wasCorrect: true
                          });
                          setShowChallengePopup(true);
                        }}
                        className="bg-orange-500 hover:bg-orange-400 text-white py-2 px-4 rounded-lg text-sm font-bold" 
                      >
                        🚩 Challenge
                      </button>
                      )}
                      <button
                        onClick={handleReviewQuestions}
                        className="bg-blue-700 hover:bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-bold"
                      >
                        📋 Review
                      </button>
                    </div>
                  </>
                )}
                
                <div className="mt-6">
                  
                </div>
              </>
            ) : viewOnlyAnswer ? (
              // VIEW-ONLY MODE - Show answer after Skip to Next (NO POINTS)
              // ✏️ OPTIMIZED: Reduced padding for phone screens
              <div
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                className="flex-1 flex flex-col items-center"
              >
                {/* Show question with correct answer highlighted */}
                <div className="bg-gray-800 rounded-lg p-4 mb-3 text-center w-full max-w-[340px]">
                  <p className="text-yellow-400 text-xs font-bold mb-1">QUESTION:</p>
                  <p className="text-white text-base font-semibold">
                    {currentQ.question_text}
                  </p>
                </div>
                
                <div className="bg-red-600 rounded-lg p-3 mb-3 w-full max-w-[340px]">
                  <p className="text-white text-base font-bold text-center">
                    ✗ Skipped
                  </p>
                  <p className="text-red-200 text-xs text-center mt-1">
                    No points awarded
                  </p>
                </div>
                
                {/* Just show correct answer - no grid */}
                <div className="bg-green-600 rounded-lg p-3 mb-3 w-full max-w-[340px] border-2 border-yellow-400">
                  <p className="text-yellow-300 text-xs text-center mb-1">✓ Correct Answer:</p>
                  <p className="text-white text-lg font-bold text-center">{currentQ.correct_answer}</p>
                </div>
                
                {/* Continue section */}
                <div className="w-full max-w-[340px]">
                  <p className="text-white text-sm text-center mb-2 animate-pulse">
                    ⬅️ Swipe or tap to continue
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setViewOnlyAnswer(false);
                        handleNextQuestion();
                      }}
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-lg"
                    >
                      ⬅️ Continue
                    </button>
                    {/* 🔧 FIX 2025.11.28: Delay showing End Game button */}
                    {showActionButtons && (
                      <button
                        onClick={handleEndGame}
                        onMouseEnter={() => setHoveringButton(true)}
                        onMouseLeave={() => setHoveringButton(false)}
                        className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2"
                      >
                        <span>💾</span>
                        <span>End Game</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // NORMAL MODE - Show correct/incorrect with scoring
              <>
                {/* Result box - 340px width */}
                <div className={`${isCorrect ? 'bg-green-600' : 'bg-red-600'} rounded-lg p-4 mx-auto w-full max-w-[340px]`}>
                  <p className="text-white text-lg font-bold text-center">
                    {isCorrect ? '✓ CORRECT!' : '✗ Brain glitch! 🤪'}
                  </p>
                  {isCorrect && !isRetry && (() => {
                    const earnedTags = Math.floor((currentQ.difficulty_points || currentQ.tag_value || 10) * modeConfig[safeCurrentMode].tagsMultiplier);
                    return <p className="text-green-200 text-sm text-center mt-1">+{earnedTags} {earnedTags === 1 ? 'tag' : 'tags'} earned!</p>;
                  })()}
                </div>
                
                {isCorrect ? (
                  <div className="w-full max-w-[340px] mx-auto mt-3 space-y-2">
                    <p className="text-white text-center text-sm animate-pulse">
                      ⬅️ Swipe or tap to continue
                    </p>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleNextQuestion}
                        className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-lg"
                      >
                        ⬅️ Continue
                      </button>
                      {/* 🔧 FIX 2025.11.28: Delay showing these buttons */}
                      {showActionButtons && (
                        <>
             {localStorage.getItem('trak_token') && (
                          <button
                           onClick={handleOpenChallenge} 
                            onMouseEnter={() => setHoveringButton(true)}
                            onMouseLeave={() => setHoveringButton(false)}
                            className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2"
                          >
                            <span>🚩</span>
                            <span>Question Challenge</span>
                          </button>
                          )}
                          <button
                            onClick={handleEndGame}
                            onMouseEnter={() => setHoveringButton(true)}
                            onMouseLeave={() => setHoveringButton(false)}
                            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2"
                          >
                            <span>💾</span>
                            <span>End Game</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  // 🔧 FIX 2026.01.08.1800: Match screenshot design with buy-back option
                  <div className="w-full max-w-[340px] mx-auto mt-3">
                    {/* Instruction text */}
                    <p className="text-white text-center text-sm mb-3">
                      Watch an ad to retry or skip and lose your TRAKs!
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      {/* Watch Ad & Retry */}
                      <button
                        onClick={handleWatchAd}
                        className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-lg"
                      >
                        📺 Watch Ad & Retry
                        <span className="block text-green-200 text-xs">Get back {savedTraks} TRAKs</span>
                      </button>
                      
                      {/* 🆕 2026.01.08: Buy Back with TRAKs (25% cost) */}
                      {savedTraks > 0 && (
                        <button
                          onClick={handleBuyBackRetry}
                          className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 px-4 rounded-lg"
                        >
                          💎 Buy Back & Retry
                          <span className="block text-purple-200 text-xs">Pay {Math.floor(savedTraks * 0.25)} TRAKs (25%) to try again</span>
                        </button>
                      )}
                      
                      {/* Skip & Lose TRAKs */}
                      <button
                        onClick={handleSkipToNew}
                        className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold py-3 px-4 rounded-lg"
                      >
                        🔥 Skip & Lose {savedTraks} TRAKs
                      </button>
                      
                      {/* End Game */}
                      <button
                        onClick={handleEndGame}
                        className="w-full bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-4 rounded-lg"
                      >
                        🚪 End Game
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* TRAK and tag scores at bottom - Only show on answer/result screens, NOT in Group Quiz */}
      {(showAnswer || gameEnded || quizEnded) && !groupQuizMode && (
        <div className="bg-gray-900 p-2 text-center">
          <p className="text-white text-xs">
            {correctInGame}/{attemptedInGame} • {traksInGame === 1 ? 'TRAK' : 'TRAKs'}: {traksInGame} • Total: {(playerData?.stats?.totalTraks || 0) + traksInGame} • {tagsInGame === 1 ? 'tag' : 'tags'}: {tagsInGame} • Total: {(playerData?.stats?.totalTags || 0) + tagsInGame}
          </p>
        </div>
      )}
      
      {/* Clean banner ad at very bottom */}
      
      {/* Ask a Pal Platform Chooser Modal */}
      {showPalChooser && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-white text-2xl font-bold mb-2 text-center">👥 Ask a Pal</h3>
            <p className="text-gray-300 text-sm mb-6 text-center">
              How would you like to ask for help?
            </p>
            
            <div className="space-y-3 mb-4">
              {/* WhatsApp Option */}
              <button
                onClick={() => handlePlatformChoice('whatsapp')}
                className="w-full bg-green-600 hover:bg-green-500 text-white py-4 px-4 rounded-lg flex items-center gap-3 transition-all"
              >
                <span className="text-2xl">💚</span>
                <div className="flex-1 text-left">
                  <div className="font-bold">WhatsApp</div>
                  <div className="text-xs text-green-200">Ask a specific friend privately</div>
                </div>
              </button>
              
              {/* Facebook Option */}
              <button
                onClick={() => handlePlatformChoice('facebook')}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 px-4 rounded-lg flex items-center gap-3 transition-all"
              >
                <span className="text-2xl">📘</span>
                <div className="flex-1 text-left">
                  <div className="font-bold">Facebook</div>
                  <div className="text-xs text-blue-200">Post to your friends publicly</div>
                </div>
              </button>
              
              {/* Copy to Clipboard Option */}
              <button
                onClick={() => handlePlatformChoice('copy')}
                className="w-full bg-gray-600 hover:bg-gray-500 text-white py-4 px-4 rounded-lg flex items-center gap-3 transition-all"
              >
                <span className="text-2xl">📋</span>
                <div className="flex-1 text-left">
                  <div className="font-bold">Copy to Clipboard</div>
                  <div className="text-xs text-gray-300">Copy question to share anywhere</div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowPalChooser(false)}
              className="w-full bg-red-600 hover:bg-red-500 text-white py-3 px-4 rounded-lg font-semibold"
            >
              Cancel
            </button>
            
            <p className="text-yellow-300 text-xs text-center mt-4">
              ⚠️ Using this feature keeps TRAK for 50% of value
            </p>
          </div>
        </div>
      )}
      
      {/* Challenge Popup Form - INTEGRATED FROM REFERENCE FILE */}
      {showChallengePopup && lastAttemptedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto">
            <h3 className="text-white text-xl font-bold mb-4">🔍 Eagle eyes!</h3>
            <p className="text-gray-300 text-sm mb-4">
              Spotted a questionable question? Report it and earn 50 tags if our team confirms it needs fixing!
            </p>
            
            {/* Show the question being challenged */}
            <div className="bg-gray-700 p-3 rounded mb-4">
              <p className="text-yellow-300 text-xs font-bold mb-1">QUESTION BEING CHALLENGED:</p>
              <p className="text-white text-sm mb-2">{lastAttemptedQuestion.questionText}</p>
              <p className="text-gray-400 text-xs">
                <strong>Correct answer:</strong> {lastAttemptedQuestion.correctAnswer}
              </p>
              <p className="text-gray-400 text-xs">
                <strong>Your answer:</strong> {lastAttemptedQuestion.playerAnswer} 
                {lastAttemptedQuestion.wasCorrect ? ' ✓' : ' ✗'}
              </p>
            </div>
            
            <div className="mb-4">
              <p className="text-white text-sm font-semibold mb-2">What's the issue?</p>
              <div className="space-y-2">
                {['Wrong answer marked correct', "Question doesn't make sense", 'Offensive content', 'Spelling/grammar mistake', 'Other'].map((issue) => (
                  <label key={issue} className="flex items-center text-white text-sm cursor-pointer hover:bg-gray-700 p-2 rounded">
                    <input
                      type="radio"
                      name="issue"
                      value={issue}
                      checked={challengeIssue === issue}
                      onChange={(e) => setChallengeIssue(e.target.value)}
                      className="mr-3"
                    />
                    {issue}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-white text-sm font-semibold mb-2 block">Tell us more (optional):</label>
              <textarea
                value={challengeDetails}
                onChange={(e) => setChallengeDetails(e.target.value)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg text-sm"
                rows="3"
                placeholder="Additional details..."
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSubmitChallengeClick}
                disabled={!challengeIssue}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                  challengeIssue 
                    ? 'bg-yellow-600 hover:bg-yellow-500 text-white' 
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                Send Challenge
              </button>
              <button
                onClick={() => {
                  setShowChallengePopup(false);
                  setChallengeIssue('');
                  setChallengeDetails('');
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
            </div>
            
            {!challengeIssue && (
              <p className="text-yellow-300 text-xs text-center mt-2">
                ⚠️ Please select an issue type above to enable the Send button
              </p>
            )}
          </div>
        </div>
      )}
      </>
      )}
      
      {/* ============================================
          FIXED BANNER AD - 340x50 (same as Picture Grid)
          Always visible at bottom for maximum impressions
          🔧 FIX 2025.11.27.1730: Updated to 340x50 to match Picture Grid
          🔧 FIX 2025.11.27.1740: Added key for refresh on screen change
          🔧 FIX 2025.12.03: Removed innerHTML to prevent React DOM conflicts
          ============================================ */}
      {!gameEnded && !groupQuizMode && (
        <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-gray-900 z-50 px-2 pb-2">
          <BannerAd 
            selectedGame={selectedGame}
            currentQuestionIndex={currentQuestionIndex}
          />
        </div>
      )}
      
      {/* ============================================
          GROUP QUIZ BANNER AD - 340x50
          🔧 FIX 2025.11.28.1230: REMOVED - Group Quiz always shows interstitial ad instead
          ============================================ */}
      {/* No bottom banner in Group Quiz - interstitial is always shown */}
    </div>
  );
}

// ============================================================================
// 🔧 FIX 2025.12.03: BannerAd component uses React state instead of innerHTML
// This prevents the "removeChild" DOM conflict error
// ============================================================================
const BannerAd = ({ selectedGame, currentQuestionIndex }) => {
  const [imageError, setImageError] = React.useState(false);
  
  // Reset error state when URL changes
  React.useEffect(() => {
    setImageError(false);
  }, [selectedGame?.mobileBannerUrl]);
  
  if (!selectedGame?.mobileBannerUrl || imageError) {
    return (
      <div 
        className="h-12 flex items-center justify-center bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded shadow-lg" 
        style={{width: '340px', margin: '0 auto'}}
      >
        <span className="text-white text-xs font-bold">📺 Advertisement 340x50</span>
      </div>
    );
  }
  
  return (
    <a 
      href={selectedGame.adClickUrl || 'https://traktag.com'} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block"
      style={{width: '340px', margin: '0 auto'}}
    >
      <img 
        key={`player-banner-${currentQuestionIndex}`}
        src={selectedGame.mobileBannerUrl} 
        alt="Advertisement"
        className="mx-auto rounded shadow-lg"
        style={{width: '340px', height: '50px', objectFit: 'cover'}}
        onError={() => setImageError(true)}
      />
    </a>
  );
};

export default Section2WithIDLabels;
